import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit, Renderer2,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {DomSanitizer, Title} from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ConsoleTourComponent } from '@netfoundry-ui/feature/console-tour';
import { ZitiWasmService } from '@netfoundry-ui/shared/ziti-wasm';
import { SideNavigatorService } from '@netfoundry-ui/feature/navigator';
import { AccountService, BillingEnabledService, BillingService } from '@netfoundry-ui/feature/shared-services';
import {
  EndpointServiceV2,
  NETWORK_SERVICE,
  NetworkServiceV2, ProcessExecutionServiceV2,
  ZitiControllerService,
} from '@netfoundry-ui/shared/apiv2';
import {
    AuthorizationService,
    AuthService,
    IamService,
    IdletimeoutService,
    LOGIN_SERVICE,
    LoginService,
} from '@netfoundry-ui/shared/authorization';
import { ClientSyncsService } from '@netfoundry-ui/shared/client-syncs';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    _VERSION,
    AzureSubscription,
    Customer,
    ENVIRONMENT,
    Environment,
    Network,
    NetworkGroup,
    NetworkV2,
    Tenant,
} from '@netfoundry-ui/shared/model';

import {
  ApiService,
  AzureSubscriptionService,
  BrandingModeService,
  ClientService,
  CustomerService,
  ErrorHistoryService,
  FeatureService,
  IdentityPreferencesService,
  INFRASTRUCTURE_URLS,
  LoggerService,
  MenuService,
  NetworkGroupService,
  NetworkService,
  OrganizationPreferencesService,
  RefresherService,
  SessionTimerService,
  TokenService,
  URLS,
  ZITI_URLS,
  ZitiEnabledService,
} from '@netfoundry-ui/shared/services';
import { ShareComponent } from '@netfoundry-ui/shared/share';
import { ConfirmComponent } from '@netfoundry-ui/ui/confirm';
import { SortbyPipe } from '@netfoundry-ui/ui/pipes';
import { CookieService } from 'ngx-cookie-service';
import {BehaviorSubject, catchError, firstValueFrom, forkJoin, Subscription, tap} from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { of } from 'rxjs';
import _, { cloneDeep, first, get, defer, delay, head, isEmpty, toLower } from 'lodash';
import moment from 'moment';
import { SurveyComponent } from '@netfoundry-ui/feature/survey';

import { ZendeskWidgetService } from '@netfoundry-ui/shared/services';
import {ExtensionService, SETTINGS_SERVICE, SettingsService, SHAREDZ_EXTENSION, ZITI_DOMAIN_CONTROLLER} from 'ziti-console-lib';
import {SelectNetworkModalComponent} from "@netfoundry-ui/feature/select-network-modal";
import {V2NetworkCreationFormComponent} from "@netfoundry-ui/feature/form/v2-network-creation-form";
import {Organization} from '@netfoundry-ui/shared/model';
import { NetworkControllerInfoComponent } from '@netfoundry-ui/feature/network-controller-info';
import { HybridCloudRegistrationAlertComponent } from '@netfoundry-ui/feature/hybrid-cloud-registration-alert';
import { PrecheckDetailsComponent } from '@netfoundry-ui/feature/precheck-details';
import { OverlayContainer } from '@angular/cdk/overlay';

const EnjoyHint = get(window, 'EnjoyHint', (p?: any) => {});

declare const gtag;

// @ts-expect-error: import reference to the ZAC growler instance
const { growler } = window;

// interval for the refresher to be called
const loginCheckTimeout = 30000;

const dnsValidationSteps = ["Validate Public Custom Domain Name", "Validate Public Custom Domain Challenge Record Exists", "Validate Custom Domain Name"];
const dnsValidationSessionString = "close-dns-validation-";
const createNetworkString = "Create Network";
const hybridCloudSessionString = "close-hybrid-cloud-connection-check-";
const hybridCloudConnectionCheckString = "Check management connection to controller host";
const verifyHostConnectivityString = "Verify host image and outbound connectivity"
const hostConnectivitySessionString = "close-verify-host-image-alert-";

const DNS_ALERT = "dnsAlert";
const HYBRID_CLOUD_CONNECTION_ALERT = "hybridCloudConnectionAlert";
const HOST_CONNECTIVITY_ALERT = "hostConnectivityAlert";

/* Pages that paint main with --formBackground, the colour the projectable form pages
 * use. They keep the app page header and the tabs, so the shared form wrapper cannot
 * paint it for them the way it does on a create page. Their sections then read like
 * the sections on a create page: a white fill against a shaded page, no drawn edge. */
const FORM_SHADED_PAGES = [
    // Network Settings, Operations and Alerts are not here: they draw their own header on the raised canvas.
    URLS.SUPPORT_OPERATIONS,
    // The FD listings (HTTP / TCP / IPSec / Agents / Processes / Alerts / Audit / Operations tabs) are NOT
    // here on purpose: each is new-gen and draws its own lib-list-table-header on the shaded canvas (isShaded
    // in setupSubtitle), so it must not also take the lighter formShaded background. Their create/detail FORM
    // routes do stay formShaded, via the DETAIL_PAGES patterns below.
    // FD Settings is the exception: both its tabs are nf-panel settings pages like the network Settings.
    URLS.FD_SETTINGS,
    URLS.FD_SETTINGS_AI_ASSISTANT,
    // Users, User Roles and Role Templates are drawn like the FD listings: an nf-panel list with a detail drawer beside it.
    URLS.USERS_LIST,
    URLS.ROLE_TEMPLATES,
    URLS.ROLE_TEMPLATES_LIST,
    URLS.USER_ROLES_LIST,
    // Backups is a listing, so it stays on the plain page background.

    // The certificate upload and renewal pages are drawn in nf-panel sections like the token page that sends somebody to
    // the upload page, so they take the same background. They are public, but this getter reads the path only and does
    // not ask who is looking.
    URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR,
    URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V2,
    URLS.FRONTDOOR_CLIENT_CERTIFICATES_CSR_V3,
    URLS.FRONTDOOR_CLIENT_CERTIFICATES_RENEW,
];

/* The url patterns that take the form background, matched where a literal path cannot say
 * it. The managed component detail pages — /network-controllers/{id} and
 * /network/edge-routers/{id} — render the projectable form sections, so they take it. The
 * list page above each one is a table, so it stays unshaded. /customers/{id} is drawn the
 * same way. Its sibling customer-process-executions is a table, so the pattern excludes it.
 *
 * The FD sub-app's form pages are here for the other reason: they draw their own header and
 * so fall to `setupSubtitle`'s default branch, which paints nothing. One pattern per page
 * covers `create` and an id alike, because writing a share and editing one are the same
 * page and must not be two colours. The TCP Shares pattern excludes the two certificate
 * tabs: they are listings with a case of their own in `setupSubtitle`, and they are named
 * in FORM_SHADED_PAGES with the other FD listings. The pattern under them catches the token
 * form, which draws its own header the way the share form does. */
// Beta lists draw their own header, tabs and search with lib-list-table, so the shell's are hidden on them.
const BETA_LIST_PAGES: string[] = [
    URLS.CUSTOMERS_LIST,
    URLS.CUSTOMER_PROCESS_EXECUTIONS_LIST,
    URLS.ON_PREM_LIST,
    URLS.NETWORK_CONTROLLERS_LIST,
    URLS.ALLOCATED_IPS_LIST,
    URLS.IPSEC_TUNNELERS_LIST,
    URLS.SCIM_LIST,
    URLS.PROCESS_EXECUTIONS_LIST,
    URLS.MANAGEMENT_EVENTS_LIST,
    URLS.NETWORK_EVENTS_LIST,
    URLS.NOTIFICATIONS_LIST,
    URLS.OLD_NETWORK_LIST,
    INFRASTRUCTURE_URLS.MANAGED_ROUTERS_LIST,
    ZITI_URLS.ZITI_SERVICE_TEMPLATES_LIST,
    URLS.USERS_LIST,
    URLS.USER_ROLES_LIST,
    URLS.ROLE_TEMPLATES_LIST,
    URLS.TOKENS_LIST,
    URLS.MANAGE_INVITATIONS_LIST,
    URLS.STORED_SECRETS_LIST,
    URLS.ORGANIZATIONS_LIST,
    URLS.OIDC_ISSUERS_LIST,
    URLS.OIDC_AUDIENCES_LIST,
    URLS.OIDC_IDENTITY_AUDIENCE_MAP_LIST,
    URLS.OIDC_PUBLIC_CLIENT_ORG_MAPPING_LIST,
];

const DETAIL_PAGES = [
    /^\/network-controllers\/(?!list$)[^/]+$/,
    /^\/network\/edge-routers\/(?!list$)[^/]+$/,
    /^\/customers\/(?!customer-process-executions|onboard$|list$)[^/]+$/,
    /^\/fd\/http-shares\/(?!domain-names$|oidc-providers$)[^/]+$/,
    /^\/fd\/http-shares\/oidc-providers\/[^/]+$/,
    /^\/fd\/tcp-shares\/(?!certificates$|certificate-request-tokens$|server-certificates$|server-certificate-requests$)[^/]+$/,
    /^\/fd\/tcp-shares\/certificate-request-tokens\/[^/]+$/,
    /^\/fd\/tcp-shares\/certificates\/[^/]+$/,
    /^\/fd\/tcp-shares\/server-certificates\/[^/]+$/,
    /^\/fd\/tcp-shares\/server-certificate-requests\/[^/]+$/,
    /^\/fd\/ipsec\/(shares|tunnels)\/[^/]+$/,
    /^\/fd\/agents\/[^/]+$/,
    // The role template form page draws its own header, like the FD form pages.
    /^\/user-identities\/role-templates\/[^/]+$/,
];

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    /**
     * Template Elements V2
     *
     * I am using this area to organize items I know are required for the new design.
     *
     * We should start cleaning up old and unused stuff maybe Rev 2 post templat change
     */
    tabs = '';
    page = '';
    isFilterable = true;
    isLoggedIn = false;
    isZACView = false;
    showLoader = false;
    area = '';
    currentNavItem: any = { isMOPView: true };
    isNavOpen = false;
    isHeader = true;
    showLegacySearch = false;
    isTimeSearchAvailable = false;
    isNotDialLogs = true;
    closeTimeout;
    @ViewChild(ShareComponent, { static: true }) share: ShareComponent;
    location: Location;
    title = 'NetFoundry Console';
    subtitle = 'Console';
    network = '';
    breadcrumb = '';
    bannerMessage = '';
    bannerMessageZiti = '';
    processBannerMessage = '';
    processBannerType = '';
    bannerClass = '';
    showBanner = false;
    showBannerZiti = false;
    showSupportLink = false;
    showProcessBanner = false;
    bannerButtonText = 'Contact';
    resourceType;
    actionTitle = '';
    showTabs = false;
    networks = [];
    networkInit = new BehaviorSubject<boolean>(false);
    url = '';
    isMenuOpen = false;
    isSelfService = false;
    isSelfServiceTeams = false;
    showQuickStart = false;
    showUserTabs = false;
    showProfileTabs = false;
    tenantLabel = '';
    domain = '';
    brandingIsSet = false;
    orgInit = false;
    tenantInit = false;
    customerInit = false;
    isShaded = false;
    noPadding = false;
    networkLimits;
    currentAccount;
    currentNetwork: Network | NetworkV2 = new Network({});
    currentOrg: NetworkGroup = new NetworkGroup({});
    previousNetwork: Network | NetworkV2 = new Network({});
    organizationList = [];
    orgLookup = {};
    hasAzureSubscription = false;
    isFirstLoad = true;
    isFirstAdd = true;
    isFirstPage = false;
    dialogRef;
    confirmDialog;
    idleTimeoutWarningActive = false;
    tenantId;
    _hideSideBar = false;
    shortBreadCrumbs = false;
    isEnterprise = false;
    showBannerSpinner = false;
    isGrowlersOpen = false;
    initRoute = '';
    ignoredRoutes = ['/callback','/login', '/'];
    showExpandCollapseIcon = false;
    private growlerServiceSub: Subscription;
    private currentNetworkSub = new Subscription();
    private azureSubSub = new Subscription();
    private networkServiceSub = new Subscription();
    private subscription = new Subscription();
    private selfLinkSub: Subscription = new Subscription();
    private lastErrorSub = new Subscription();
    private hasNoNetworks = false;
    /* Networking Controls From Header */
    private networkServiceGetSub: Subscription = new Subscription();
    // variable to hold the refresher
    private loginCheckRefresher;
    private gettingStartedWasShown = false;
    private billingInvalid = false;
    private wasPricingObtained = false;
    private werePermissionsObtained = false;
    private wasAzureSubscriptionInitialized = false;
    private currentSubscription: any;
    private currentPaymentProfile: any;
    private currentContract: any;
    private processRefresher;
    public processActive = false;
    private processRefreshInterval = 10000;
    private darkModeUrl;
    public pageIsUnauthed;
    public appLoading = true;
    private titleName = '';

    private alertStep;
    private dnsAlertIsController = false;




    constructor(
        private logger: LoggerService,
        private router: Router,
        private route: ActivatedRoute,
        private refresher: RefresherService,
        @Inject(LOGIN_SERVICE) public login: LoginService,
        private growlService: GrowlerService,
        private accountService: AccountService,
        public apiService: ApiService,
        private networkService: NetworkService,
        @Inject(NETWORK_SERVICE) private networkServiceV2: NetworkServiceV2,
        private changeDetector: ChangeDetectorRef,
        private iamService: IamService,
        public dialogForm: MatDialog,
        private idleTimeout: IdletimeoutService,
        public brandingService: BrandingModeService,
        public featureService: FeatureService,
        public billingEnabledService: BillingEnabledService,
        private titleService: Title,
        private azureSubscriptionService: AzureSubscriptionService,
        private zenDeskService: ZendeskWidgetService,
        private billingService: BillingService,
        private cookieService: CookieService,
        private zitiEnabledService: ZitiEnabledService,
        public authorizationService: AuthorizationService,
        private menuService: MenuService,
        private organizationService: NetworkGroupService,
        private sortByPipe: SortbyPipe,
        private errorHistoryService: ErrorHistoryService,
        private authService: AuthService,
        private clientSyncService: ClientSyncsService,
        private tokenService: TokenService,
        private clientService: ClientService,
        private sessionTimerService: SessionTimerService,
        private endpointServiceV2: EndpointServiceV2,
        private identityPreferencesService: IdentityPreferencesService,
        private organizationPreferencesService: OrganizationPreferencesService,
        private customerService: CustomerService,
        private processExecutionService: ProcessExecutionServiceV2,
        private renderer: Renderer2,
        private sanitizer: DomSanitizer,
        @Inject(SETTINGS_SERVICE) public settingsService: SettingsService,
        @Inject(SHAREDZ_EXTENSION) private sharedZExtensionService: ExtensionService,
        @Inject(ZITI_DOMAIN_CONTROLLER) public zitiControllerService: ZitiControllerService,
        @Inject(ENVIRONMENT) public environment: Environment,
        @Inject(_VERSION) private version: any,
        private overlayContainer: OverlayContainer,
        public zitiWasmService: ZitiWasmService,
        private sideNavigatorService: SideNavigatorService,
    ) {
      this.addGreyscale();
      this.brandingService.initCustomBrandingUrl();
      this.initDarkmodeUrl();
      if (this.login.isVanityUrl) {
        this.initOrgBranding();
      }
      this.subscription.add(
        this.login.authenticationChanged.subscribe((isLoggedIn) => {
          if (isLoggedIn) {
            this.initOrgBranding();
          }
        })
      );
      this.subscription.add(
        this.router?.events?.subscribe((event) => {
          if (event instanceof NavigationEnd) {
            this.pageIsUnauthed = this.login.pageIsUnauthenticated();
            if (this.pageIsUnauthed) {
              this.removeGreyscale();
              this.appLoading = false;
            }
          }
        })
      );
    }

    // function for keeping the user from timing out
    @HostListener('click', [])
    @HostListener('document:keydown', [])
    @HostListener('window:scroll', [])
    @HostListener('document:mousemove', [])
    onUserMovement() {
        if (this.isLoggedIn && !this.idleTimeoutWarningActive) {
            // if the user is logged in and the user clicks their mouse, scrolls the page, clicks a key, or moves their mouse
            //  reset the idle timeout
            this.idleTimeout.initIdleTimeout();
        }
    }

    handleMouseOver(event: any) {
      defer(() => {
        this.showExpandCollapseIcon = true;
        if (!this.menuService.locked) {
          this.menuService.resetArea();
        }
      })
    }

    get hideSideBar() {
      const page = this.url.split(';')[0].split('?')[0].split('#')[0];
      return this._hideSideBar || page === URLS.INVITATION || this.pageIsUnauthed
    }

    get isFormShaded(): boolean {
        if (!this.url) {
            return false;
        }
        const path = this.url.split(';')[0].split('?')[0].split('#')[0];
        return FORM_SHADED_PAGES.includes(path) || DETAIL_PAGES.some((page) => page.test(path));
    }

    /** Full-page identity / service-policy conflict view: scroll inside page, not under app-list-header */
    get isConflictPage(): boolean {
        if (!this.url) {
            return false;
        }
        const path = this.url.split(';')[0].split('?')[0].split('#')[0];
        return /\/conflict$/.test(path);
    }

    get showAppLoader() {
      return (this.zitiControllerService.loadingZitiController && this.isZACView) || this.login.loginProcessing;
    }

    async ngOnInit() {
        this.initRoute = window?.location?.pathname;
        await this.zenDeskService.initZendesk();
        this.subscription.add(
            // this.sharedZExtensionService.consoleActionTriggered?.subscribe((event: any) => {
            //     this.trackZitiMenuAction(event);
            // })
        );
        this.initZacGrowlerHandler();
        this.refreshProcesses();
        this.titleName = this.environment.titleName || 'NetFoundry';
        this.titleService.setTitle(this.titleName);
        const currentUri = window.location?.pathname + window.location?.search;
        this.subscription.add(() => {
            this.apiService.currentUserPreferences.subscribe((userPreferences) => {
                if (isEmpty(userPreferences)) {
                    return;
                }
                const page = this.url.split(';')[0].split('?')[0].split('#')[0];
                this.setupSubtitle(page);
            });
        });

        this.subscription.add(
            this.tokenService.logout.subscribe(() => {
                this.growlService.show(
                    new GrowlerData(
                        'error',
                        'Error',
                        'Session Expired',
                        'Your session has expired, please login again to continue'
                    )
                );
                this.login.logout(false, true);
            })
        );

        this.zenDeskService.zE('webWidget', 'updateSettings', {
            webWidget: { zIndex: 999999 },
        });
        this.zenDeskService.zE('webWidget', 'hide');
        this.isNavOpen = this.menuService.areaId != null && this.menuService.areaId.length > 0;
        this.subscription.add(
            this.menuService.area.subscribe((area) => {
                this.area = area;
                this.isNavOpen = area.length > 0;
                this.handleCloudZitiAssets();
                if (this.isZACView) {
                  this.checkForZitiSession();
                }
            })
        );
        this.subscription.add(
            this.menuService.navItemChanged.subscribe((navItem) => {
                this.currentNavItem = navItem;
                this.handleCloudZitiAssets();
            })
        );
        if (this.environment['faviconUrl'] != null) {
            this.setFavicon(this.environment['faviconUrl']);
        }

        // Skip loading organization data for public pages like CSR generation
        const currentPath = window.location.pathname;
        const isPublicPage = currentPath.includes('/frontdoor/client-certificates-tokens');


            this.subscription.add(
                this.apiService.currentTenant.subscribe((tenant) => {
                this.appLoading = false;
                localStorage.setItem('tenantId', tenant.id);
                this.tenantId = tenant.id;
                this.tenantLabel = tenant.label;
                const html = document.getElementsByTagName('body')[0];

                this.brandingService.fetchBrandCss().then((rawCss) => {
                    html.style.cssText = rawCss;
                    this.brandingIsSet = true;
                });

                if (this.featureService.isNewSelfServiceWorkflow(this.tenantLabel)) {
                    this.showGettingStarted();
                }

                if (this.tenantId === this.environment.pldtId) {
                    this.idleTimeout.setTimeoutInterval(this.environment.pldtTimeoutSeconds);
                }
            })
        );

            this.subscription.add(
                this.apiService.currentOrg.subscribe((org) => {
                    this.currentOrg = org;
                    this.appLoading = false;
                })
            );

            this.subscription.add(
                this.accountService.currentAccount.subscribe((account) => {
                    this.currentAccount = account;
                    this.isEnterprise = account?.billingMode === 'Enterprise';
                    this.isSelfService = account?.billingMode === 'SelfService';
                    this.appLoading = false;
                    if (!isEmpty(account.id)) {
                        this.billingService
                            .getContractsByAccountId(account.id)
                            .toPromise()
                            .then((contracts: any) => {
                                this.currentContract = head(contracts);
                                this.apiService.setCurrentContract(this.currentContract);
                            });
                    }
                })
            );

        this.subscription.add(
            this.apiService.currentSubscription.subscribe((subscription) => {
                this.appLoading = false;
                this.currentSubscription = subscription;
                this.subscriptionChanged(subscription);
            })
        );

        this.subscription.add(
            this.apiService.currentPaymentProfile.subscribe((paymentProfile) => {
                this.currentPaymentProfile = paymentProfile;
                this.paymentProfileChanged(paymentProfile);
            })
        );

        this.subscription.add(
            this.billingEnabledService.isBillingEnabled.subscribe((billingEnabled) => {
                this.setBillingEnabled(billingEnabled);
            })
        );

        this.login.handleVanityUrl();
        this.login.handleAuthenticationWithHash();

        this.router.events.subscribe((event) => {
            defer(() => {
                this.navigationChanged(event);
            });
        });
        // setting a check to validate the user is logged in to run every 30 seconds
        this.loginCheckRefresher = setInterval(() => {
            // checking if the user was previously logged in. if they were not, it doesn't make sense to try to log them out
            if (this.isLoggedIn) {
                // checking whether or not the user is logged in
                this.isLoggedIn = this.login.isAuthenticated();
                // if the auth check fails, logging the user out
                if (!this.isLoggedIn) {
                    this.logger.info('Session token is no longer valid, logging out');
                    this.login.logout(false);
                    this.zenDeskService.zE('webWidget', 'hide');
                }
            }
        }, loginCheckTimeout);
        // Watch the current network
        this.currentNetworkSub.add(
            this.apiService.currentNetwork.subscribe((network) => {
                if (isEmpty(network?.id)) {
                    this.setTempOrganization();
                    return;
                }
                this.previousNetwork = cloneDeep(this.currentNetwork);
                if (this.breadcrumb === 'Dashboard') {
                    if (network.name) {
                        this.titleService.setTitle('NetFoundry: ' + network.name);
                    } else {
                        this.titleService.setTitle('NetFoundry Console');
                    }
                }

                // this.currentNetworkName = network.name;
                this.currentNetwork = network as unknown as NetworkV2;
                const organizationId = this.currentNetwork.networkGroupId;
                // @FIX ME - we should clean this up, but the feature service doesn't work quite right if we remove it -MG
                if (this.currentOrg.id == null || this.currentOrg.id !== organizationId) {
                    this.logger.info('attempting to set current org');
                    if (organizationId != null) {
                        this.setOrganization(organizationId);
                    } else {
                        this.setTempOrganization();
                    }
                }
                if (this.router.url === URLS.HOME || this.router.url === URLS.OLD_HOME) {
                    this.subtitle = this.currentNetwork.name !== undefined ? this.currentNetwork.name : 'Console';
                    this.titleService.setTitle('NetFoundry: ' + this.subtitle);
                }
                this.initNetworkLimits();
                this.handleBannerMessageVisibility();
                this.handleEditPageExit();
                this.getExecutions();
            })
        );
        this.lastErrorSub.add(
            this.apiService.lastError.subscribe((error) => {
                const traceId = error.headers.get('x-b3-traceid');
                let errorMessage = error.message;
                if (error.error && (typeof error.error === 'string' || error.error instanceof String)) {
                    const errorObject = JSON.parse(String(error.error));
                    if (errorObject && errorObject[0] && errorObject[0]['message']) {
                        errorMessage = errorObject[0]['message'];
                    } else if (errorObject && errorObject.message) {
                        errorMessage = errorObject.message;
                    }
                } else if (error.error && error.error[0] && error.error[0]['message']) {
                    errorMessage = error.error[0]['message'];
                } else if (error.error && error.error.detail) {
                    errorMessage = error.error.detail;
                }
                let errorHistoryMessage = errorMessage;
                if (traceId != null) {
                    errorHistoryMessage += ' - ' + traceId;
                }
                this.errorHistoryService.addError(errorHistoryMessage);
                this.growlService.show(new GrowlerData('error', 'Error', 'An error occurred', errorMessage));
            })
        );
        this.lastErrorSub.add(
            this.iamService.lastError.subscribe((error) => {
                const traceId = error.headers.get('x-b3-traceid');
                let errorMessage = error.message;
                if (error.error && error.error.message) {
                    errorMessage = error.message;
                }
                let errorHistoryMessage = errorMessage;
                if (traceId != null) {
                    errorHistoryMessage += ' - ' + traceId;
                }
                this.errorHistoryService.addError(errorHistoryMessage);
                this.growlService.show(new GrowlerData('error', 'Error', 'An error occurred', errorMessage));
            })
        );
        this.lastErrorSub.add(
            this.authService.lastError.subscribe((error) => {
                const traceId = error.headers.get('x-b3-traceid');
                let errorMessage = error.message;
                if (error.error && error.error.message) {
                    errorMessage = error.message;
                }
                let errorHistoryMessage = errorMessage;
                if (traceId != null) {
                    errorHistoryMessage += ' - ' + traceId;
                }
                this.errorHistoryService.addError(errorHistoryMessage);
                this.growlService.show(new GrowlerData('error', 'Error', 'An error occurred', error.message));
            })
        );
        this.lastErrorSub.add(
            this.clientSyncService.lastError.subscribe((error) => {
                const traceId = error.headers.get('x-b3-traceid');
                let errorMessage = error.message;
                if (error.error && error.error.message) {
                    errorMessage = error.message;
                }
                let errorHistoryMessage = errorMessage;
                if (traceId != null) {
                    errorHistoryMessage += ' - ' + traceId;
                }
                this.errorHistoryService.addError(errorHistoryMessage);
                this.growlService.show(new GrowlerData('error', 'Error', 'An error occurred', error.message));
            })
        );

        // subscribing to the timeout warning alert. when this subscription is triggered and the alert is true
        //  then the user should be warned that they are about to be logged out due to being idle
        this.subscription.add(
            this.idleTimeout.timeoutWarningAlert.subscribe((timeoutAlert) => {
                if (timeoutAlert === true) {
                    this.idleTimeoutWarningActive = true;
                    let interval;
                    let unit;
                    if (this.environment.defaultTimeoutWarningSeconds < 121) {
                        unit = 'seconds';
                        interval = this.environment.defaultTimeoutWarningSeconds;
                    } else {
                        unit = 'minutes';
                        interval = Math.floor(this.environment.defaultSessionTimeoutSeconds / 60);
                    }
                    const data = {
                        title: 'Your Session is About to Timeout',
                        appendId: 'AppComponent',
                        subtitle: `Are you still there? You have ${interval} ${unit} before being signed off. Would you like to stay signed in?`,
                        icon: 'Timeout',
                        action: 'Yes Please',
                        cancelAction: 'No, sign me out',
                        isTimeoutWarning: true,
                    };
                    this.confirmDialog = this.dialogForm.open(ConfirmComponent, {
                        data: data,
                        height: '340px',
                        width: '600px',
                        autoFocus: false,
                    });
                    this.confirmDialog.beforeClosed().subscribe((result) => {
                        // if the result has a property loggingOut, rather than being just a boolean value, the user is being
                        //  logged out of the console and we should close the dialog without continuing
                        if (result === undefined) {
                            this.idleTimeout.initIdleTimeout();
                            this.idleTimeoutWarningActive = false;
                        } else if (result['loggingOut'] === undefined) {
                            if (result) {
                                this.idleTimeout.initIdleTimeout();
                                this.idleTimeoutWarningActive = false;
                            } else {
                                this.idleTimeout.clearIdleTimeout();
                                this.login.logout(false);
                                this.zenDeskService.zE('webWidget', 'hide');
                            }
                        }
                    });
                }
            })
        );
        this.subscription.add(
          this.settingsService.settingsChange.subscribe((settings) => {
            this.checkForZitiSession();
          })
        );
        this.overlayContainer.getContainerElement().classList.add('mopView');
    }

    private trackZitiMenuAction({ action, page, item }: any) {
        if (action === 'edit' || !(window as any)?.gtag || !page) {
            return;
        }
        const pageKey = `${page}`.replace(/-/g, '_');
        gtag('event', `${action}_${page}`, {
            [`${pageKey}_name`]: item?.name,
            [`${pageKey}_id`]: item?.id,
        });
    }

    navigationChanged(event) {
        if (event instanceof NavigationStart) {
            clearTimeout(this.refresher.refreshTimerId);
        }
        if (event instanceof NavigationEnd) {
            if ((window as any).gtag) {
                gtag('event', 'page_view', {
                    page_path: event.urlAfterRedirects,
                    page_title: document.title,
                });
            }
            /* The fragment goes too. It is not part of the route, and the enrollment link carries
             * its JWT there — left on, the whole credential is printed into the page header. */
            const page = event.url.split(';')[0].split('?')[0].split('#')[0];
            this.page = page;
            const newIsLoggedIn = this.login.isAuthenticated();
            this.pageIsUnauthed = this.login.pageIsUnauthenticated();
            if (this.pageIsUnauthed) {
              this.removeGreyscale();
              this.appLoading = false;
            }
            // checking if the user is actually logged in
            if (this.isLoggedIn && !newIsLoggedIn && !this.pageIsUnauthed) {
                // if the user's token is no longer valid, logging them out
                this.logger.info('Session token is no longer valid, logging out: ', event.url);
                this.isLoggedIn = false;
                // clearing the timeout before logging out
                this.subtitle = 'Login';
                this.idleTimeout.clearIdleTimeout();
                this.login.logout(false);
                this.zenDeskService.zE('webWidget', 'hide');
                this.werePermissionsObtained = false;
                this.wasAzureSubscriptionInitialized = false;
            } else if (this.pageIsUnauthed || page === URLS.LOGIN || page === URLS.OLD_LOGIN) {
                // The user is on a page that does not require authentication (login, the invitation
                // review screen, etc.). Don't force-logout-redirect them to /login on a
                // logged-in→out transition — just mark logged-out and skip org init (MOP-27516).
                // We don't want to redirect them from their intended destination.
                // Marking isLoggedIn as false and preventing the getOrganization call from being made
                this.isLoggedIn = false;
                // clearing the timeout before logging out
                this.idleTimeout.clearIdleTimeout();
            } else if (this.login.isLoggedIn && page !== URLS.CALLBACK) {
                // Skip init/routing while on the transient /callback page: routing the freshly
                // authenticated user to the dashboard here races the invitation accept flow and
                // briefly flashes the dashboard before a failed acceptance bounces to /invitation.
                // The destination is decided by the auth flow instead — the SDK's appState.target
                // for normal login, or auth0.service after confirmInvite for invitations. Init then
                // runs on that real destination (MOP-27516).
                if (!this.authorizationService.obtainedMopPermissions) {
                    this.initCurrentUser().then(() => {
                        this.initApplicationData(newIsLoggedIn);
                    });
                } else {
                    this.initApplicationData(newIsLoggedIn);
                }
            }
            this.url = event.url;
            this.isMenuOpen = false;
            this.showTabs = false;
            this.showUserTabs = false;
            this.showProfileTabs = false;
            this.setupSubtitle(page);
        }
    }

    initCurrentUser() {
      return this.updateCurrentUser();
    }

    initApplicationData(newIsLoggedIn) {
      forkJoin([this.updateCurrentTenant(), this.updateCurrentOrganization()]).subscribe({
        next: () => {
          this.logger.info('org and tenant info loaded');
          // An empty customer id is a valid selection: the support user picks "--No Parent--" to list
          // the top level customers. Therefore, the id cannot show whether the customer is initialized.
          // A flag is necessary, or the next navigation replaces the choice with the user's own customer.
          if (!this.customerInit) {
              this.updateCurrentCustomer();
          }
        },
        complete: () => {
          this.appLoading = false;
        }
      });
      if (!this.isLoggedIn && newIsLoggedIn) {
        // Route once permissions load, even when the user has none, so the no-role and billing
        // handling still runs. An expired token is caught earlier and never marks them as loaded.
        if (this.authorizationService.obtainedMopPermissions) {
          this.evaluateUserPermissionsAndRoute();
        } else {
          this.logger.info('Permissions not yet loaded, deferring route evaluation until permissions are obtained.');
          this.apiService.currentPermissions.pipe(
            filter(() => this.authorizationService.obtainedMopPermissions),
            take(1)
          ).subscribe(() => {
            this.evaluateUserPermissionsAndRoute();
          });
        }
      }
      this.isLoggedIn = newIsLoggedIn;
      this.applyOrgBranding();
    }

    applyOrgBranding() {
      if (this.brandingIsSet) {
        return;
      }
      const html = document.getElementsByTagName('body')[0];
      if (!this.tenantLabel) {
        this.tenantLabel = localStorage.getItem('tenantLabel');
      }
      this.brandingService.fetchBrandCss(this.tenantLabel?.toLowerCase()).then((rawCss) => {
        html.style.cssText = rawCss;
        this.brandingIsSet = true;
      });
    }

    evaluateUserPermissionsAndRoute() {
        if (
          !this.authorizationService.canListNetworks() &&
          !this.authorizationService.canListOrganizations() &&
          !this.authorizationService.canListTenants()
        ) {
          if (this.authorizationService.canViewBillingSubscriptions() || this.authorizationService.canViewPaymentProfile()) {
            this.apiService.currentUser.subscribe((user: any) => {
              this.accountService.getAccountDetailsForTenantId(user?.tenantId).then((account: any) => {
                this.isEnterprise = account.billingMode === 'Enterprise';
                this.billingEnabledService.checkBilling().then(() => {
                  this.menuService.setArea('Billing');
                  this.openBilling();
                })
              })
            });
          } else {
            // User has not serviceable role for the console
            this.logger.error('User has not serviceable role for the console. Check assigned roles on user entities page.')
          }
        } else if(this.authorizationService.canListFrontdoors()) {
          // If user doesn't have permissions to view networks, take them to Front Door (if they have FD permissions)
          if (!this.authorizationService.canListNetworks()) {
            this.menuService.setArea('Frontdoor');
            this.openFrontdoor();
          } else if (this.authorizationService.canListNetworks() && !this.networkInit.getValue()) {
            // If user does have network permissions, but the networks haven't finished loading/initializing yet, wait
            // Once networks load, and there are none, then take them to Front Door area
            this.networkInit.subscribe((networkInit) => {
              if (networkInit && this.networks.length <= 0) {
                this.menuService.setArea('Frontdoor');
                this.openFrontdoor();
              }
            });
            this.handleNetworksInitialization();
          }
        } else if (this.authorizationService.canListSoftwareAccess()) {
            if(!this.authorizationService.canListNetworks()) {
                this.menuService.setArea('On-Prem')
                this.openOnPrem()
            } else if(this.authorizationService.canListNetworks() && !this.networkInit.getValue()) {
            this.networkInit.subscribe((networkInit) => {
              if (networkInit && this.networks.length <= 0) {
                this.menuService.setArea('On-Prem');
                this.openOnPrem();
              }
            });
            this.handleNetworksInitialization();
            }
         } else if (
          this.authorizationService.canListTenants() &&
          !this.authorizationService.canListNetworks() &&
          !this.authorizationService.canListOrganizations()
        ) {
          // if the user can list tenants, don't navigate to the dashboard since they can't view it
          this.router.navigate([URLS.USERS]);
        } else {
          this.handleNetworksInitialization();
        }
    }

    handleNetworksInitialization(page = '') {
      this.showLoader = true;
      this._hideSideBar = false;
      this.shortBreadCrumbs = false;
      let identityId = null;
      const iamServiceProm = this.iamService
        .get('identities', 'self')
        .pipe(take(1))
        .toPromise()
        .then((identity) => {
          identityId = identity['id'];
          this.apiService.setCurrentUser(identity);
        });

      this.organizationService
        .get()
        .pipe(take(1))
        .toPromise()
        .then((orgs) => {
          this.orgLookup = {};
          this.organizationList = this.sortByPipe.transform(orgs, 'createdAt', 'asc');
          if (this.authorizationService.canListNetworks()) {
            this.loadNetworks();
          } else if (page === URLS.HOME || page === URLS.OLD_HOME || page === '/') {
            if (this.tenantId) {
              this.router.navigate([URLS.USERS]);
            } else {
              this.router.navigate([URLS.LOGS]);
            }
            this.showLoader = false;
          } else {
            this.showLoader = false;
          }
          for (const org of this.organizationList) {
            this.orgLookup[org.id] = org as NetworkGroup[];
          }

          this.organizationPreferencesService.getPreferences().then((result) => {
            this.logger.info('preferences', result);
            let loadingIndicatorPath;
            if (this.featureService.loadingIndicatorFile === '/assets/svgs/nf-loading-indicator.svg') {
              loadingIndicatorPath = '/assets/animations/nf-loading-indicator.json';
            } else {
              loadingIndicatorPath = '/assets/animations/neutral-loading-indicator.json';
            }
            localStorage.setItem('loading-indicator-path',loadingIndicatorPath);
            if (!result && this.tenantId) {
              this.organizationPreferencesService
                .createOrgPreference(this.tenantId)
                .subscribe((result) => result);
              this.organizationPreferencesService.getPreferences();
            }
          });
        });
      // if the user is logged in, kick off the idle timeout service
      this.idleTimeout.initIdleTimeout();
      // ensuring that the token renewal is scheduled
      this.login.scheduleRenewal();
    }

    ngOnDestroy() {
        if (this.growlerServiceSub) this.growlerServiceSub.unsubscribe();
        if (this.currentNetworkSub) this.currentNetworkSub.unsubscribe();
        if (this.networkServiceSub) this.networkServiceSub.unsubscribe();
        if (this.selfLinkSub) this.selfLinkSub.unsubscribe();
        if (this.lastErrorSub) this.lastErrorSub.unsubscribe();
        if (this.azureSubSub) this.azureSubSub.unsubscribe();
        if (this.networkServiceGetSub) this.networkServiceGetSub.unsubscribe();
        // clearing the idle timeout at the end of the component lifecycle
        this.idleTimeout.clearIdleTimeout();
        // clearing the interval on the login check refresher
        clearInterval(this.loginCheckRefresher);
    }

    bannerButtonClicked() {
        switch (this.bannerClass) {
            case 'unsupported':
                this.openSupport();
                break;
            case 'overdue':
                this.openBilling();
                break;
            case 'suspended':
                this.resumeNetwork();
                break;
        }
    }

    processBannerButtonClicked() {
        switch (this.processBannerType) {
            case DNS_ALERT:
                this.handleDnsBannerButtonClick();
                break;
            case HYBRID_CLOUD_CONNECTION_ALERT:
                this.handleHybridCloudConnectionButtonClick();
                break;
            case HOST_CONNECTIVITY_ALERT:
                this.handleVerifyHostConnectivityButtonClick();
                break;
        }


    }

    handleDnsBannerButtonClick() {
        sessionStorage.removeItem(`${dnsValidationSessionString}${this.currentNetwork.getId()}`);
        this.router.navigate([URLS.PROCESS_EXECUTIONS]);
        this.showDnsValidationInstructions(this.dnsAlertIsController);
    }

    handleHybridCloudConnectionButtonClick() {
        sessionStorage.removeItem(`${hybridCloudSessionString}${this.currentNetwork.getId()}`);
        this.showHybridCloudConnectionAlert();
    }

    handleVerifyHostConnectivityButtonClick() {
        sessionStorage.removeItem(`${hostConnectivitySessionString}${this.currentNetwork.getId()}`);
        this.showVerifyHostConnectivityAlert(this.alertStep);
    }

    openFrontdoor() {
        this.router.navigate(['/frontdoor']);
    }

    openOnPrem() {
        this.router.navigate(['/self-hosted']);
    }

    openBilling() {
        if (this.isEnterprise) {
            this.router.navigate(['/payment-profile']);
        } else {
            this.router.navigate(['/billing']);
        }
    }

    openSupport() {
        window.open('http://support.netfoundry.io');
    }

    setSuspendedBannerWarning() {
        const network: any = this.currentNetwork;
        if (this.isSelfServiceTeams) {
            const updatedDate = moment(network?.updatedAt);
            const today = moment();
            let daysLeft = 15 - Math.abs(updatedDate.diff(today, 'days'));
            if (daysLeft < 0) {
                daysLeft = 0;
            }
            this.bannerMessage = `This network has had no utilization for >7 days and has been moved to stopped state. The network will be deleted if there is no utilization in ${daysLeft} days`;
        } else {
            this.bannerMessage = `This network has been moved to stopped state since there was no utilization for 7 days. To continue service, please contact cust-support@netfoundry.io.`;
        }
        this.bannerClass = 'suspended';
        this.showBanner = true;
        this.showSupportLink =
            this.authorizationService.canUpdateNetwork(network?.id, network?.networkGroupId) &&
            !this.authorizationService.isSupportUser();
        this.bannerButtonText = 'Resume Network';
    }

    resumeNetwork() {
        let chargeWarning = '';
        if (!this.isSelfServiceTeams) {
            chargeWarning = ' Charges may apply depending on your subscription.';
        }
        const data = {
            title: 'Resume Network',
            appendId: 'AppComponentResume',
            subtitle: `Would you like to resume activity on this network?${chargeWarning}`,
            action: 'Yes Please',
            cancelAction: 'No, get me out of here',
            isTimeoutWarning: false,
        };
        this.confirmDialog = this.dialogForm.open(ConfirmComponent, {
            data: data,
            height: '200px',
            width: '600px',
            autoFocus: false,
        });
        this.confirmDialog.afterClosed().subscribe((result) => {
            if (result) {
                this.showBannerSpinner = true;
                this.bannerMessage = `Network resuming. Please wait a moment...`;
                this.showSupportLink = false;
                this.customerService
                    .selfResume()
                    .then(() => {
                        this.growlService.show(
                            new GrowlerData(
                                'success',
                                'Success',
                                `Network Resume In Progress`,
                                `Your request to resume the network was successful. Please wait a moment for the update to complete.`
                            )
                        );
                    })
                    .catch((error) => {
                        this.showBannerSpinner = false;
                        this.setSuspendedBannerWarning();
                        this.growlService.show(
                            new GrowlerData(
                                'error',
                                'Error',
                                `Network Resume Failed`,
                                `Your request to resume the network failed. Please contact support is the issue persists.`
                            )
                        );
                    });
            }
        });
    }

    close() {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
        // this.logger.info('msst');
        if (!this.menuService.locked && this.menuService.isOpen()) {
            // this.logger.info('not lockt');
            this.closeTimeout = setTimeout(() => {
                // this.logger.info('my timeout');
                this.menuService.setArea('');
            }, 2000);
        }
    }

    showGettingStarted() {
        return;
        const showSurvey = localStorage.getItem('showSurvey_' + this.tenantLabel) === 'true';
        if (showSurvey) {
            this.dialogRef = this.dialogForm.open(SurveyComponent, {
                data: {},
                height: '455px',
                width: '935px',
                autoFocus: false,
            });
            localStorage.removeItem('showSurvey_' + this.tenantLabel);
            this.menuService.setArea('');
            this.dialogRef.afterClosed().subscribe(() => {
                if (this.currentNetwork?.status === 'PROVISIONED') {
                    this.dialogRef = this.dialogForm.open(ConsoleTourComponent, {
                        data: {},
                        height: '455px',
                        width: '935px',
                        autoFocus: false,
                    });
                    this.dialogRef.afterClosed().subscribe(() => {
                        localStorage.setItem(`take-console-tour-complete_${this.tenantLabel}`, 'true');
                    });
                }
            });
        }
        this.gettingStartedWasShown = true;

        if (!this.billingInvalid) {
            this.router.navigate([URLS.GETTING_STARTED]);
        }

        localStorage.removeItem('showSurvey_' + this.tenantLabel);
        const netGroupId = get(this.organizationList, '[0].id');
        if (netGroupId && this.tenantLabel) {
            const createNewNetwork = localStorage.getItem('createNetwork_' + this.tenantLabel) === 'true';
            this.hasNoNetworks = !this.networks?.length || this.networks?.length === 0;
            if (createNewNetwork && this.hasNoNetworks) {
                // Console is no longer driving network creation
                //this.createNewNetwork();
                localStorage.removeItem('createNetwork_' + this.tenantLabel);
            }
        }
    }

    // Loads all networks
    public async loadNetworks() {
        await this._loadV7Networks();

        // if there are any networks
        if (this.networks.length > 0) {
            // check initial load
            if (this.isFirstLoad) {
                // handle some app initialization logic and check and see if we have anything stored in cookies
                this.initDefaultApp();
                const defaultApp = this.identityPreferencesService.getPreference('default_app_id');
                if (!defaultApp || defaultApp === 'networks' || defaultApp === 'infrastructure') {
                    this._initialNetworkLoad(this.networks);
                }
                // we have already loaded the initial app context, just refresh the data and make sure nothing has changed
            } else {
                let wasCurrentNetFound = false;
                for (const network of this.networks) {
                    const networkModel = this.apiService.getNetworkModel(network);
                    if (this.currentNetwork.id === networkModel.id) {
                        wasCurrentNetFound = true;
                        // set the fresh copy of the model just in case the status has changed
                        this.apiService.setCurrentNetwork(networkModel);
                        break;
                    }
                }
                // if we didn't find the network model in the list then it was probably deleted, go find the first one in the list
                if (!wasCurrentNetFound) {
                    this.apiService.setCurrentNetwork(this.networks[0]);
                }
            }
            // there are no networks in the list!
        } else {
            if (this.isFirstAdd) {
                this.isFirstAdd = false;
                // set an empty v6 network as a default
                this.apiService.setCurrentNetwork(new Network({}));
            }
        }
    }

    ngAfterViewChecked() {
        (<any>window).SetupRippler();
    }

    ngAfterViewInit() {
        // Load the HubSpot tracking script so HubSpot can deliver the NPS survey
        // (same integration as nfconsole/V7). Survey targeting — e.g. the dashboard
        // URL — is configured HubSpot-side against this portal id. Guard against a
        // missing id and double injection.
        if (!this.environment.hsId || document.getElementById('hs-script-loader')) {
            return;
        }
        const head = document.getElementsByTagName('head')[0];
        const s = document.createElement('script');
        s.id = 'hs-script-loader';
        s.type = 'text/javascript';
        s.async = true;
        s.src = `//js.hs-scripts.com/${this.environment.hsId}.js`;
        head.appendChild(s);
    }

    closeWarning() {
        this.billingEnabledService.closeWarning();
    }

    async setBillingEnabled(isBillingEnabled) {
        if (isBillingEnabled) {
            this.billingEnabledService.openWarning();
        } else {
            this.billingEnabledService.closeWarning();
        }
        if (isBillingEnabled && this.authorizationService.canListComponentTypes()) {
            this.wasPricingObtained = true;
            this.billingService.initPricingData();
        }
    }

    async checkOverdueStatus(subscription, isSelfService = false) {
        this.billingService.getOverdueStatus(subscription, isSelfService).then((result) => {
            if (result.isOverdue && result.daysOverdue >= 1 && result.daysOverdue < 45) {
                this.bannerMessage = `Invoice for the month of <b>${result.monthAndYear}</b> is due for payment. Reach us at <a href="mailto:collection@netfoundry.io">collection@netfoundry.io</a> if you have paid the invoice already.`;
                this.showBanner = true;
                this.bannerClass = 'overdue';
                this.showSupportLink = false;
                this.bannerButtonText = '';
            } else if (result.isOverdue && result.daysOverdue >= 45) {
                this.bannerMessage = `Invoice for the month of <b>${result.monthAndYear}</b> is overdue for payment. Please complete payment to avoid suspension of services. Reach us at <a href="mailto:collection@netfoundry.io">collection@netfoundry.io</a> if you have paid the invoice already.`;
                this.showBanner = true;
                this.bannerClass = 'overdue';
                this.showSupportLink = false;
                this.bannerButtonText = '';
            }
        });
    }

    getActiveSubscription(subscriptions: any) {
        if (isEmpty(subscriptions)) {
            return head(subscriptions);
        }
        let activeSub;
        try {
            for (let i = 0; i < subscriptions.length; i++) {
                const sub = subscriptions[i];
                if (toLower(sub?.status) !== 'cancelled') {
                    activeSub = sub;
                    break;
                }
            }
        } catch (err) {
            activeSub = head(subscriptions);
        }
        return activeSub;
    }

    setTempOrganization() {
        const tmpOrganizationId = get(this.organizationList, '[0].id');
        this.setOrganization(tmpOrganizationId);
    }

    private handleBannerMessageVisibility() {
        const network: any = this.currentNetwork;
        const netVersion = network?.productVersion?.charAt(0);
        const vetVersionNum = Number.parseInt(netVersion, 10);
        if (vetVersionNum === 6) {
            this.bannerMessage = `TERMINATION OF SUPPORT NOTICE: V6 is no longer supported, you are using this
                          version of our product at your own risk, click
                          <a href="https://support.netfoundry.io/hc/en-us/articles/
                          5168208798349-Notice-of-Discontinuance-and-Termination-of-Order-Form-upon-Expiration-Date"
                          target="_blank" class="banner-link">here</a> for further information.`;
            this.showBanner = true;
            this.showSupportLink = false;
            this.bannerClass = 'unsupported';
        } else if ((network as unknown as NetworkV2).status === 'SUSPENDED') {
            this.setSuspendedBannerWarning();
        } else {
            const clearBanner = this.bannerClass === 'unsupported' || this.bannerClass === 'suspended';
            if (clearBanner) {
                this.bannerMessage = '';
                this.showBanner = false;
                this.showSupportLink = false;
                this.bannerClass = '';
            }
        }
    }

    handleEditPageExit() {
        const editPageRouteRoots = [ZITI_URLS.ZITI_IDENTITIES, ZITI_URLS.ZITI_SERVICES, ZITI_URLS.ZITI_ROUTERS, ZITI_URLS.ZITI_ROUTER_POLICIES, ZITI_URLS.ZITI_SERVICE_POLICIES, ZITI_URLS.ZITI_SERVICE_ROUTER_POLICIES, ZITI_URLS.ZITI_AUTH_POLICIES, ZITI_URLS.ZITI_JWT_SIGNERS, ZITI_URLS.ZITI_CONFIGS, ZITI_URLS.ZITI_SERVICE_TEMPLATES];
        const urlSegments = this.url.split('/').filter((segment) => {
            return !isEmpty(segment);
        });
        const urlRoot = urlSegments[0];
        if (editPageRouteRoots.includes(urlRoot) || editPageRouteRoots.includes(`/${urlRoot}`)) {
            if (urlSegments.length > 1 && urlSegments[1] !== 'list') {
                this.confirmChangedNetwork(urlRoot);
            }
        }
    }

    confirmChangedNetwork(urlRoot) {
      if (this.previousNetwork.id === this.currentNetwork.id || isEmpty(this.previousNetwork.id)) {
        return;
      }
      const confirmModalData = {
        title: 'Network Changed',
        appendId: 'ChangedNetwork',
        subtitle: `Changing your network will cause you to leave this page. Would you like to continue?`,
        action: 'Close and Continue',
        cancelAction: 'No, return me to previous network',
      };
      this.confirmDialog = this.dialogForm.open(ConfirmComponent, {
        data: confirmModalData,
        height: '300px',
        width: '300px',
        autoFocus: false,
      });
      this.confirmDialog.beforeClosed().subscribe((result) => {
          if (result) {
            this.router.navigateByUrl(`/${urlRoot}`);
          } else {
            this.currentNetwork = this.previousNetwork;
            this.apiService.setCurrentNetwork(this.currentNetwork);
          }
        }
      );
    }

    private highlightQuickStartButton() {
        return;
        const showHint = localStorage.getItem('nf_show_quick_start_hint_' + this.tenantLabel) === 'true';
        if (!showHint || !this.featureService.showGettingStarted) {
            return;
        }
        const enjoy_hint_options = {
            showSkip: true,
            showNext: true,
            showPrev: true,
            btnSkipText: 'Got it',
            radius: 20,
            onEnd: () => {},
            keyCode: '',
            top: 100,
            onSkip: () => {},
        };
        const enjoyhint_instance = new EnjoyHint(enjoy_hint_options);
        const hints = [
            {
                event: 'quick-start-badge',
                selector: '.QuickStart',
                //arrowColor: 'transparent',
                margin: 0,
                shape: 'circle',
                skipButton: { className: 'quickStartSkip', text: 'Got it' },
                description:
                    '<h2>Here\'s a hint</h2><p>Use the <text style="color: #4fcbff">Quick Start</text> button at any time to return to the <text style="color: #4fcbff">Getting Started</text> page. <p>There you\'ll find links to our documentation and tutorials</p>',
                onBeforeStart: () => {},
            },
        ];
        delay(() => {
            enjoyhint_instance.set(hints);
            enjoyhint_instance.run();
        }, 100);
        localStorage.removeItem('nf_show_quick_start_hint_' + this.tenantLabel);
    }

    private static readonly FRONTDOOR_DETAIL_TITLES: { prefix: string; singular: string }[] = [
        // The FD sub-app's form page. It draws its own header, so it falls to `setupSubtitle`'s
        // default branch and only the browser tab name is left to set here.
        { prefix: '/fd/tcp-shares/', singular: 'TCP Share' },
        { prefix: '/fd/ipsec/shares/', singular: 'IPSec Share' },
        { prefix: '/fd/ipsec/tunnels/', singular: 'IPSec Tunnel' },
        { prefix: '/fd/agents/', singular: 'Agent' },
        { prefix: '/frontdoor/http-shares/', singular: 'HTTP Share' },
        { prefix: '/frontdoor/tcp-shares/', singular: 'TCP Share' },
        { prefix: '/frontdoor/ipsec-shares/', singular: 'IPSec Share' },
        { prefix: '/frontdoor/ipsec-tunnels/', singular: 'IPSec Tunnel' },
        { prefix: '/frontdoor/namespaces/', singular: 'Namespace' },
        { prefix: '/frontdoor/authentication-providers/', singular: 'Authentication Provider' },
        { prefix: '/frontdoor/health-checks/', singular: 'Health Check' },
        { prefix: '/frontdoor/client-certificates/', singular: 'Certificate' },
        { prefix: '/frontdoor/certificate-request-tokens/', singular: 'Certificate Request Token' },
        { prefix: '/frontdoor/frontends/', singular: 'Frontend' },
        { prefix: '/frontdoor/agents/', singular: 'Agent' },
        { prefix: '/user-identities/role-templates/', singular: 'Role Template' },
    ];

    /**
     * Browser tab name for a frontdoor detail route, or undefined when the url isn't one.
     */
    private getFrontdoorDetailTitle(url: string): string | undefined {
        const match = AppComponent.FRONTDOOR_DETAIL_TITLES.find((entry) => url.startsWith(entry.prefix));
        if (!match) {
            return undefined;
        }
        const segment = url.slice(match.prefix.length);
        if (isEmpty(segment) || segment.includes('/')) {
            return undefined;
        }
        return segment === 'create' || segment === 'enroll' ? `New ${match.singular}` : match.singular;
    }

    /** These pages draw their own header on the shaded canvas, so the app header and its tab strip step aside. */
    private useNetworkPageHeader() {
        this.isHeader = false;
        this.showTabs = false;
        this.isShaded = true;
    }

    private setupSubtitle(url: string) {
        this.isHeader = true;
        this.actionTitle = '';
        this.isShaded = false;
        this.isTimeSearchAvailable = false;
        this.resourceType = undefined;
        this.noPadding = false;
        this.showLegacySearch = false;
        this.shortBreadCrumbs = false;
        if (url.startsWith('/ssh-terminal')) {
            this.isHeader = false;
            this.isFilterable = false;
            this.showTabs = false;
            this.tabs = '';
            return;
        }
        switch (url) {
            case URLS.ZITIROUTERS:
          case INFRASTRUCTURE_URLS.MANAGED_ROUTERS:
            case INFRASTRUCTURE_URLS.MANAGED_ROUTERS_LIST:
            case INFRASTRUCTURE_URLS.ROUTERS: {
                this.subtitle = 'Edge Routers';
                this.actionTitle = 'Add An Edge Routers';
                this.breadcrumb = this.subtitle;
                this.resourceType = 'edge-router';
                this.isFilterable = true;
                this.tabs = 'controllers';
                break;
            }
            case URLS.IPSEC_TUNNELERS:
            case URLS.IPSEC_TUNNELERS_LIST: {
                // Match the Edge Routers header chrome — title, breadcrumb, tabs
                // strip, and action button context all come from <app-list-header>
                // rather than being drawn inline. tabs = 'ipsec-tunnelers' lights
                // up the single-tab strip in list-header.component.html, which is
                // what positions the horizontal divider line in the same place
                // routers gets it (under the tabs).
                this.subtitle = 'IPsec Tunnelers';
                this.actionTitle = 'Add an IPsec Tunneler';
                this.breadcrumb = this.subtitle;
                this.resourceType = 'ipsec-tunneler';
                this.isFilterable = true;
                this.tabs = 'ipsec-tunnelers';
                break;
            }
            case URLS.BILLING: {
                this.subtitle = 'Billing Dashboard';
                this.breadcrumb = 'Billing Dashboard';
                this.tabs = 'billing';
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.BILLING_DASHBOARD: {
                this.subtitle = 'Billing Dashboard';
                this.breadcrumb = 'Billing Dashboard';
                this.tabs = 'app-billing';
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.ORG_BILLING: {
              this.subtitle = 'Billing Dashboard';
              this.breadcrumb = 'Billing Dashboard';
              this.tabs = 'org-billing';
              this.isShaded = true;
              this.isFilterable = false;
              break;
            }
            case URLS.SUPPORT_BILLING: {
              this.subtitle = 'Billing Dashboard';
              this.breadcrumb = 'Billing Dashboard';
              this.tabs = 'support-billing';
              this.isShaded = true;
              this.isFilterable = false;
              break;
            }
            case URLS.OLD_LOGS:
            case URLS.LOGS: {
                this.subtitle = 'Organization Events';
                this.breadcrumb = 'Organization Events';
                this.tabs = 'orgLogs';
                this.actionTitle = 'Export';
                this.isFilterable = true;
                this.isTimeSearchAvailable = true;
                break;
            }
            case URLS.OLD_OLD_SETTINGS:
            case URLS.OLD_SETTINGS:
            case URLS.SETTINGS: {
                this.subtitle = 'Organization Settings';
                this.breadcrumb = 'Organization Settings';
                this.tabs = 'organization';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.OLD_SUBSCRIPTIONS:
            case URLS.SUBSCRIPTIONS: {
                this.subtitle = 'Endpoint Lifecycle';
                this.breadcrumb = 'Endpoint Lifecycle';
                this.tabs = 'integrations';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.CLIENT_SYNC_AAD: {
                this.subtitle = 'Azure Active Directory';
                this.breadcrumb = 'Manage AAD';
                this.tabs = 'integrations';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.CLIENT_SYNC_AD: {
                this.subtitle = 'Active Directory';
                this.breadcrumb = 'Manage AD';
                this.tabs = 'integrations';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.IDENTITIES_BASE:
            case URLS.OLD_OLD_USERS:
            case URLS.OLD_USERS:
            case URLS.USER_ROLES:
            case URLS.USERS: {
                this.subtitle = 'Users';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A User';
                this.tabs = 'users';
                this.isFilterable = true;
                break;
            }
            case URLS.USERS_LIST: {
                this.subtitle = 'Users';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A User';
                this.tabs = 'users';
                // The list table draws its own search box.
                this.isFilterable = false;
                break;
            }
            // The tabs of the Users ribbon keep the header of the left nav item, as the FD ribbons do. The tab strip shows which tab is open.
            case URLS.ROLE_TEMPLATES:
            case URLS.ROLE_TEMPLATES_LIST: {
                this.subtitle = 'Users';
                this.breadcrumb = 'Role Templates';
                this.actionTitle = 'Add A Role Template';
                this.tabs = 'users';
                // The list table draws its own search box.
                this.isFilterable = false;
                break;
            }
            case URLS.USER_ROLES_LIST: {
                this.subtitle = 'Users';
                this.breadcrumb = 'User Roles';
                this.actionTitle = 'Add A Default Role';
                this.tabs = 'users';
                // The list table draws its own search box.
                this.isFilterable = false;
                break;
            }
            case URLS.USER: {
                this.subtitle = 'User Admin';
                this.breadcrumb = this.subtitle;
                this.actionTitle = '';
                this.tabs = 'user';
                this.isFilterable = true;
                break;
            }
            case URLS.OLD_DAILYSTATS:
            case URLS.OLD_RESOURCE_USAGE:
            case URLS.RESOURCE_USAGE:
            case URLS.DAILY_ENDPOINT_UTILIZATION:
            case URLS.DAILYSTATS: {
                this.subtitle = 'Reporting and Analytics';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Download CSV';
                this.tabs = 'reporting';
                this.isFilterable = false;
                this.isShaded = false;
                break;
            }
            case URLS.OLD_NETWORK_GROUP_SUMMARY:
            case URLS.NETWORK_GROUP_SUMMARY: {
                this.subtitle = 'Reporting and Analytics';
                this.breadcrumb = this.subtitle;
                this.tabs = 'reporting';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_SUBSCRIPTIONS: {
                this.subtitle = 'Billing Subscriptions';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.APP_BILLING_SUBSCRIPTIONS: {
                this.subtitle = 'Billing Subscriptions';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.ORG_BILLING_SUBSCRIPTIONS: {
                this.subtitle = 'Billing Subscriptions';
                this.breadcrumb = this.subtitle;
                this.tabs = 'org-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.SUPPORT_BILLING_SUBSCRIPTIONS: {
                this.subtitle = 'Billing Subscriptions';
                this.breadcrumb = this.subtitle;
                this.tabs = 'support-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.PAYMENT_PROFILE: {
                this.subtitle = 'Billing Management';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_PAYMENT_PROFILE: {
                this.subtitle = 'Billing Management';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.ORG_PAYMENT_PROFILE:
                this.subtitle = 'Billing Management';
                this.breadcrumb = this.subtitle;
                this.tabs = 'org-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            case URLS.SUPPORT_PAYMENT_PROFILE:
                this.subtitle = 'Billing Management';
                this.breadcrumb = this.subtitle;
                this.tabs = 'support-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            case URLS.ACCOUNT_DETAILS: {
                this.subtitle = 'Account Details';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_ACCOUNT_DETAILS: {
                this.subtitle = 'Account Details';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.ORG_ACCOUNT_DETAILS: {
                this.subtitle = 'Account Details';
                this.breadcrumb = this.subtitle;
                this.tabs = 'org-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.SUPPORT_ACCOUNT_DETAILS: {
                this.subtitle = 'Account Details';
                this.breadcrumb = this.subtitle;
                this.tabs = 'support-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_SUBSCRIPTION_OVERAGES: {
                this.subtitle = 'Billing Subscription Overages';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_SUBSCRIPTION_INVOICES: {
                this.subtitle = 'Billing Subscription Invoices';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = false;
                break;
            }
            case URLS.APP_BILLING_SUBSCRIPTION_OVERAGES: {
                this.subtitle = 'Billing Subscription Overages';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.APP_BILLING_SUBSCRIPTION_INVOICES: {
                this.subtitle = 'Billing Subscription Invoices';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = false;
                break;
            }
            case URLS.ORG_BILLING_SUBSCRIPTION_OVERAGES: {
                this.subtitle = 'Billing Subscription Overages';
                this.breadcrumb = this.subtitle;
                this.tabs = 'org-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.SUPPORT_BILLING_SUBSCRIPTION_OVERAGES: {
                this.subtitle = 'Billing Subscription Overages';
                this.breadcrumb = this.subtitle;
                this.tabs = 'support-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_SUBSCRIPTION_USAGE: {
                this.subtitle = 'Billing Subscription Usage';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.APP_BILLING_SUBSCRIPTION_USAGE: {
                this.subtitle = 'Billing Subscription Usage';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.ORG_BILLING_SUBSCRIPTION_USAGE: {
                this.subtitle = 'Billing Subscription Usage';
                this.breadcrumb = this.subtitle;
                this.tabs = 'org-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.SUPPORT_BILLING_SUBSCRIPTION_USAGE: {
                this.subtitle = 'Billing Subscription Usage';
                this.breadcrumb = this.subtitle;
                this.tabs = 'support-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.BILLING_SUBSCRIPTION_USAGE_REPORT: {
                this.subtitle = 'Usage Report';
                this.breadcrumb = this.subtitle;
                this.tabs = 'billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.APP_BILLING_SUBSCRIPTION_USAGE_REPORT: {
                this.subtitle = 'Usage Report';
                this.breadcrumb = this.subtitle;
                this.tabs = 'app-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.ORG_BILLING_SUBSCRIPTION_USAGE_REPORT: {
                this.subtitle = 'Usage Report';
                this.breadcrumb = this.subtitle;
                this.tabs = 'org-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.SUPPORT_BILLING_SUBSCRIPTION_USAGE_REPORT: {
                this.subtitle = 'Usage Report';
                this.breadcrumb = this.subtitle;
                this.tabs = 'support-billing-management';
                this.isFilterable = false;
                this.isShaded = true;
                break;
            }
            case URLS.OLD_MANAGE_INVITATIONS:
            case URLS.MANAGE_INVITATIONS:
            case URLS.MANAGE_INVITATIONS_LIST: {
                // one header for the Users ribbon, as on Role Templates
                this.subtitle = 'Users';
                this.breadcrumb = 'Invitations';
                this.actionTitle = 'Add A Invitations';
                this.tabs = 'users';
                this.isFilterable = true;
                this.showLegacySearch = true;
                break;
            }
            case URLS.ROLES: {
                this.subtitle = 'Roles & Permissions';
                this.actionTitle = 'Add A Role';
                this.breadcrumb = 'Roles';
                this.isFilterable = true;
                this.tabs = 'roles';
                break;
            }
            case URLS.STORED_SECRETS:
            case URLS.STORED_SECRETS_LIST: {
                this.subtitle = 'Stored Secrets';
                this.actionTitle = 'Add A Stored Secret';
                this.breadcrumb = 'Stored Secrets';
                this.isFilterable = true;
                this.tabs = 'stored_secrets';
                break;
            }
            case URLS.OIDC_ISSUERS:
            case URLS.OIDC_ISSUERS_LIST: {
                this.subtitle = 'OIDC Issuer';
                this.actionTitle = 'Add An OIDC Issuer';
                this.breadcrumb = 'OIDC Issuer';
                this.isFilterable = true;
                this.tabs = 'oidc_audiences';
                break;
            }
            case URLS.OIDC_IDENTITY_AUDIENCE_MAP:
            case URLS.OIDC_IDENTITY_AUDIENCE_MAP_LIST: {
                this.subtitle = 'Oidc Identity Audience Maps';
                this.actionTitle = 'Add An Oidc Identity Audience Map';
                this.breadcrumb = 'Oidc Identity Audience Maps';
                this.isFilterable = true;
                this.tabs = 'oidc_audiences';
                break;
            }
            case URLS.OIDC_AUDIENCES:
            case URLS.OIDC_AUDIENCES_LIST: {
                this.subtitle = 'Oidc Audiences';
                this.actionTitle = 'Add An Oidc Audience';
                this.breadcrumb = 'Oidc Audiences';
                this.isFilterable = true;
                this.tabs = 'oidc_audiences';
                break;
            }
            case URLS.OIDC_PUBLIC_CLIENT_ORG_MAPPING:
            case URLS.OIDC_PUBLIC_CLIENT_ORG_MAPPING_LIST: {
                this.subtitle = 'OIDC Public Client To Org Mapping';
                this.actionTitle = 'Add An Org to Public Client Map';
                this.breadcrumb = 'Oidc Organization to Public Client Map';
                this.isFilterable = true;
                this.tabs = 'oidc_audiences';
                break;
            }
            case URLS.OLD_TOKENS:
            case URLS.TOKENS:
            case URLS.TOKENS_LIST: {
                this.subtitle = 'Api Accounts';
                this.breadcrumb = this.subtitle;
                this.isFilterable = true;
                this.actionTitle = 'Add Api';
                this.tabs = 'tokens';
                break;
            }
            case URLS.OLD_PROFILE:
            case URLS.PROFILE: {
                this.subtitle = 'Profile';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.isShaded = true;
                this.tabs = 'profile';
                break;
            }
            case URLS.USER_SETTINGS:
            case URLS.OLD_BRANDING:
            case URLS.BRANDING: {
                this.subtitle = 'User Settings';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.isShaded = true;
                this.tabs = 'profile';
                break;
            }
            case URLS.NOTIFICATIONS:
            case URLS.NOTIFICATIONS_LIST: {
                this.subtitle = 'manage Notifications';
                this.actionTitle = 'Add A Notification';
                this.breadcrumb = this.subtitle;
                this.isFilterable = true;
                this.showTabs = true;
                this.tabs = 'events';
                break;
            }
            case INFRASTRUCTURE_URLS.NETWORK_GROUPS:
            case URLS.NETWORK_GROUPS: {
                this.subtitle = 'Network Groups';
                this.actionTitle = 'Add A Network Group';
                this.breadcrumb = this.subtitle;
                this.isFilterable = true;
                this.showTabs = true;
                this.tabs = 'network-groups';
                break;
            }
            case INFRASTRUCTURE_URLS.NETWORKS:
            case URLS.OLD_NETWORK:
            case URLS.OLD_NETWORK_LIST:
            case URLS.NETWORKS_COMBINED:
            case URLS.NETWORK: {
                this.subtitle = 'Networks';
                this.actionTitle = 'Add A Network';
                this.breadcrumb = this.subtitle;
                this.isFilterable = true;
                this.showTabs = true;
                this.tabs = 'networks';
                break;
            }
            case URLS.NETWORK_CONTROLLERS:
            case URLS.NETWORK_CONTROLLERS_LIST: {
                this.subtitle = 'Network Controllers';
                this.actionTitle = 'Add A Network Controller';
                this.breadcrumb = this.subtitle;
                this.isFilterable = true;
                this.showTabs = true;
                this.tabs = 'controllers';
                break;
            }
            case INFRASTRUCTURE_URLS.OPERATIONS: {
                this.subtitle = 'Management';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                // setupSubtitle never clears this.tabs, so a page that sets no tab
                // group keeps the previous page's tab bar. Always name the group.
                this.tabs = 'operations';
                this.useNetworkPageHeader();
                break;
            }
            case INFRASTRUCTURE_URLS.OPERATIONS_UPGRADES: {
                this.subtitle = 'Upgrades';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'operations';
                this.useNetworkPageHeader();
                break;
            }
            case INFRASTRUCTURE_URLS.OPERATIONS_BACKUPS: {
                this.subtitle = 'Backups';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'operations';
                this.useNetworkPageHeader();
                break;
            }
            case INFRASTRUCTURE_URLS.OPERATIONS_SUPPORT_INFO: {
                this.subtitle = 'Support Info';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'operations';
                this.useNetworkPageHeader();
                break;
            }
            case INFRASTRUCTURE_URLS.SETTINGS: {
                this.subtitle = 'Settings';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'settings';
                this.useNetworkPageHeader();
                break;
            }
            case INFRASTRUCTURE_URLS.SETTINGS_AI_ASSISTANT: {
                this.subtitle = 'AI Assistant';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'settings';
                this.useNetworkPageHeader();
                break;
            }
            case INFRASTRUCTURE_URLS.SETTINGS_FIREWALL_INFO: {
                this.subtitle = 'Firewall Info';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'settings';
                this.useNetworkPageHeader();
                break;
            }
            /* Two tabs over one subject: what is wrong now, and why nobody is being told
             * about it. The filters that narrow either listing are on the pages
             * themselves, so the legacy search stays off for both. */
            case URLS.ALERTS: {
                this.subtitle = 'Alerts';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'alerts';
                this.useNetworkPageHeader();
                break;
            }
            case URLS.MUTES: {
                this.subtitle = 'Mutes';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'alerts';
                this.useNetworkPageHeader();
                break;
            }
            /* The third tab of the same strip: what counts as wrong here. Its own Create
             * button lives on the panel rather than in this header, because the two form
             * pages are children of the alerts module and this header is not drawn over
             * them. */
            case URLS.LATENCY_POLICIES: {
                this.subtitle = 'Latency Policies';
                /* Set because the page carries an <app-action-button>, and the header
                 * reads this to decide where the breadcrumb sits: an empty actionTitle
                 * slides it to the right edge, straight under the round button. Every
                 * other page with a create button sets one for the same reason. */
                this.actionTitle = 'Add A Component Policy';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'alerts';
                this.useNetworkPageHeader();
                break;
            }
            /* The fourth tab, set up like Latency Policies: the create button is on the panel. */
            case URLS.OFFLINE_POLICIES: {
                this.subtitle = 'Offline Policies';
                this.actionTitle = 'Add A Component Policy';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'alerts';
                this.useNetworkPageHeader();
                break;
            }
            case URLS.ALLOCATED_IPS:
            case URLS.ALLOCATED_IPS_LIST: {
                this.subtitle = 'Allocated Ips';
                this.actionTitle = 'Add An Allocated Ip';
                this.breadcrumb = this.subtitle;
                this.isFilterable = true;
                this.showTabs = true;
                this.tabs = 'controllers';
                break;
            }
            case URLS.PROCESS_EXECUTIONS:
            case URLS.PROCESS_EXECUTIONS_LIST: {
                this.subtitle = 'Network Processes';
                this.actionTitle = 'View and manage processes for the current network';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'processes';
                if (this.featureService.disableNewNavigation) {
                    this.tabs = 'networks';
                } else {
                    this.tabs = 'processes';
                }
                break;
            }
            case URLS.SCIM:
            case URLS.SCIM_LIST: {
                this.subtitle = 'Integrations';
                this.actionTitle = 'View and manage processes for the current network';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'scim-integrations';
                break;
            }
            case ZITI_URLS.ZITI_SERVICE_TEMPLATES:
            case ZITI_URLS.ZITI_SERVICE_TEMPLATES_LIST: {
                this.subtitle = 'Templates';
                this.actionTitle = 'Add A Template';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.tabs = 'service-templates';
                break;
            }
            case URLS.OLD_ORGANIZATIONS:
            case URLS.ORGANIZATIONS:
            case URLS.ORGANIZATIONS_LIST: {
                this.subtitle = 'Organizations';
                this.actionTitle = 'Add An Organization';
                this.breadcrumb = 'Organizations';
                this.isFilterable = true;
                this.tabs = 'organizations';
                break;
            }
            case URLS.PASSWORD: {
                this.subtitle = 'Password';
                this.breadcrumb = 'User Preferences <span class="slash">/</span> ' + this.subtitle;
                this.isFilterable = false;
                this.showProfileTabs = true;
                this.showTabs = true;
                break;
            }
            case URLS.V7EVENTS:
            case URLS.OLD_EVENTS:
            case URLS.MANAGEMENT_EVENTS:
            case URLS.MANAGEMENT_EVENTS_LIST: {
                this.subtitle = 'Management Events';
                this.breadcrumb = 'Management Events';
                this.isFilterable = true;
                this.actionTitle = 'Export';
                this.tabs = 'events';
                this.isTimeSearchAvailable = true;
                this.isNotDialLogs = true;
                break;
            }
            case URLS.NETWORK_EVENTS:
            case URLS.NETWORK_EVENTS_LIST: {
                this.subtitle = 'Network Events';
                this.breadcrumb = 'Network Events';
                this.isFilterable = true;
                this.actionTitle = 'Export';
                this.tabs = 'events';
                this.isTimeSearchAvailable = true;
                this.isNotDialLogs = true;
                break;
            }
            case URLS.OLD_ALARMS:
            case URLS.ALARMS: {
                this.subtitle = 'Network Alarms';
                this.breadcrumb = 'Network Alarms';
                this.isFilterable = true;
                this.actionTitle = 'Export';
                this.tabs = 'events';
                this.isTimeSearchAvailable = true;
                break;
            }
            case URLS.OLD_LOGIN:
            case URLS.LOGIN: {
                this.subtitle = 'Login';
                this.isLoggedIn = false;
                this.isFilterable = false;
                this.showTabs = false;
                this.showBanner = false;
                this.showQuickStart = false;
                break;
            }
            case URLS.PAGENOTFOUND: {
                this.subtitle = 'Page Not Found';
                this.isLoggedIn = false;
                this.isFilterable = false;
                this.showTabs = false;
                break;
            }
            case URLS.ERRORS: {
                this.subtitle = 'Errors';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                break;
            }
            case URLS.ZITI_CERTIFICATE_AUTHORITIES: {
                this.subtitle = 'Certificate Authorities';
                this.breadcrumb = 'Certificate Authorities';
                this.isShaded = false;
                this.isFilterable = true;
                this.actionTitle = 'Add A CA';
                this.tabs = 'ca';
                break;
            }
            case INFRASTRUCTURE_URLS.METRICS:
            case URLS.METRICS: {
              this.subtitle = 'Metrics';
              this.breadcrumb = 'Metrics';
              this.isShaded = false;
              this.isFilterable = false;
              this.showTabs = false;
              this.tabs = 'metrics';
              break;
            }
            case URLS.FABRIC_LATENCY: {
                this.subtitle = 'Fabric Latency';
                this.breadcrumb = this.subtitle;
                this.tabs = 'metrics';
                this.isShaded = true;
                this.isFilterable = false;
                this.showTabs = true;
                break;
            }
            case URLS.FABRIC_LATENCY_TIMEOUTS: {
                this.subtitle = 'Fabric Latency Timeouts';
                this.breadcrumb = this.subtitle;
                this.tabs = 'metrics';
                this.isShaded = false;
                this.isFilterable = false;
                this.showTabs = true;
                break;
            }
            case URLS.TRAFFIC_ANALYSIS: {
              this.subtitle = 'Traffic Analysis';
              this.breadcrumb = this.subtitle;
              this.tabs = 'metrics';
              this.isFilterable = false;
              this.showTabs = true;
              // The beta page draws its own header/tabs and sits on the shaded canvas; the legacy
              // view keeps the app-level header on the default (white) main.
              const beta = this.featureService.betaFeaturesEnabled;
              this.isShaded = beta;
              this.isHeader = !beta;
              break;
            }
            case URLS.MICROSEGMENTS:
            case URLS.MICROSEGMENTS + '/traffic-analysis': {
              this.subtitle = 'Microsegments';
              this.breadcrumb = this.subtitle;
              this.isHeader = false;
              this.isFilterable = false;
              this.showTabs = false;
              this.isShaded = this.featureService.betaFeaturesEnabled;
              break;
            }
            case URLS.SERVICE_SELECT:
            case URLS.SERVICE_SIMPLE:
            case URLS.SERVICE_SOURCE_IDENTITIES:
            case URLS.SERVICE_CREATE_FROM_TRAFFIC: {
                // Service create pages (the beta chooser, Simple Service form, and the composable
                // create-from-traffic page, plus the legacy ziti-console forms at the same URLs) all
                // draw their own header, so hide the app header and tab strip. The new-gen beta pages
                // sit on the shaded canvas; the legacy forms stay on the plain white main.
                this.subtitle = 'Create a Service';
                this.breadcrumb = this.subtitle;
                this.isHeader = false;
                this.isFilterable = false;
                this.showTabs = false;
                this.isShaded = this.featureService.betaFeaturesEnabled;
                break;
            }
            case URLS.CONTROLLER_LATENCY: {
                this.subtitle = 'Controller Latency';
                this.breadcrumb = this.subtitle;
                this.tabs = 'metrics';
                this.isShaded = true;
                this.isFilterable = false;
                this.showTabs = true;
                break;
            }
            case URLS.SAMPLE_SERVICE:
            case URLS.GETTING_STARTED: {
                this.isShaded = true;
                this.noPadding = true;
                this.isFilterable = false;
                this.showTabs = false;
                this.tabs = '';
                this.isHeader = false;
                break;
            }
                case URLS.CUSTOMERS: {
                this.subtitle = 'Manage Customers';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Onboard Customer';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = false;
                this.tabs = 'customers';
                break;
            }
            case URLS.CUSTOMER_PROCESS_EXECUTIONS:
            case URLS.CUSTOMER_PROCESS_EXECUTIONS_LIST: {
                this.subtitle = 'Customer Processes';
                this.breadcrumb = this.subtitle;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'customers';
                break;
            }
            case URLS.CUSTOMERS_ONBOARD: {
                // A create page, the same as the frontdoor ones: the form draws its own
                // lib-form-header, so the app page header stays off.
                this.subtitle = 'Onboard New Customer';
                this.breadcrumb = this.subtitle;
                this.isHeader = false;
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = false;
                this.tabs = '';
                break;
            }
                case URLS.ON_PREM: {
                this.subtitle = 'Service Accounts';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A Service Account';
                this.isShaded = false;
                this.noPadding = true;
                this.isFilterable = false;
                this.showTabs = false;
                this.tabs = '';
                break;
            }
            // The FD sub-app. Short breadcrumbs on purpose: an FD page is scoped by the frontdoor the user
            // picked, not by a network, so the default trail would name one that has nothing to do with what
            // is on screen. Every FD listing case below is new-gen: it draws its own lib-list-table-header
            // (with its own tab strip, where it has one) on the shaded canvas, so the shell header and tab
            // strip stay off, and subtitle is kept only for the browser-tab title.
            case URLS.FD_TCP_SHARES: {
                this.subtitle = 'TCP Shares';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_HTTP_SHARES: {
                this.subtitle = 'HTTP Shares';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_DOMAIN_NAMES: {
                this.subtitle = 'Domain Names';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_OIDC_PROVIDERS: {
                this.subtitle = 'OIDC Providers';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_TCP_SHARES_CERTIFICATES: {
                this.subtitle = 'Client Certificates';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_TCP_SHARES_TOKENS: {
                this.subtitle = 'Certificate Request Tokens';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_TCP_SHARES_SERVER_CERTIFICATES: {
                this.subtitle = 'Server Certificates';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_TCP_SHARES_SERVER_CERTIFICATE_REQUESTS: {
                this.subtitle = 'Server Certificate Requests';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_IPSEC: {
                // One header covers both tabs; the mounted listing (Shares or Tunnels, by the `tab` query param) draws it.
                this.subtitle = 'IPSec';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_AGENTS: {
                this.subtitle = 'Agents';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_PROCESSES: {
                this.subtitle = 'Processes';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_DASHBOARD: {
                /* The dashboard draws its own header, like the Network overview. */
                this.subtitle = 'Dashboard';
                this.isHeader = false;
                this.isFilterable = false;
                this.breadcrumb = '';
                this.showTabs = false;
                break;
            }
            case URLS.FD_ALERTS: {
                this.subtitle = 'Alerts';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_AUDIT_EVENTS: {
                // Read-only list like Alerts. Had no case before, so it fell to `default` (no header at all).
                this.subtitle = 'Audit';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_OPERATIONS_HTTP:
            case URLS.FD_OPERATIONS_MTLS:
            case URLS.FD_OPERATIONS_MANAGEMENT: {
                this.subtitle = 'Operations';
                this.breadcrumb = '';
                this.isHeader = false;
                this.showTabs = false;
                this.isShaded = true;
                this.isFilterable = false;
                break;
            }
            case URLS.FD_SETTINGS:
            case URLS.FD_SETTINGS_AI_ASSISTANT: {
                /* One header for both tabs of the Settings page. */
                this.subtitle = 'Settings';
                /* No trail, for the reason given on the TCP Shares case above. */
                this.breadcrumb = '';
                /* No action title: each tab edits one configuration and never creates anything. */
                this.shortBreadCrumbs = true;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'fd-settings';
                break;
            }
                case URLS.FRONTDOOR: {
                this.subtitle = 'Frontdoor Shares';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A Share';
                this.isShaded = false;
                this.noPadding = true;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'frontdoor';
                break;
            }
                case URLS.FRONTDOOR_TCP: {
                this.subtitle = 'TCP Configuration';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A Share';
                this.isShaded = false;
                this.noPadding = true;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'frontdoor';
                break;
            }
            case URLS.FRONTDOOR_HTTP: {
                this.subtitle = 'HTTP Configuration';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A Share';
                this.isShaded = false;
                this.noPadding = true;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'frontdoor';
                break;
            }
            case URLS.FRONTDOOR_IPSEC_SHARES: {
                this.subtitle = 'IPSec Configuration';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add A Share';
                this.isShaded = false;
                this.noPadding = true;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'frontdoor';
                break;
            }
            case URLS.FRONTDOOR_AGENTS: {
                this.subtitle = 'Agents';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Enroll An Agent';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'agents';
                break;
            }
            case URLS.FRONTDOOR_PROCESS_EXECUTIONS: {
                this.subtitle = 'Change Log';
                this.breadcrumb = this.subtitle;
                this.actionTitle = '';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'change-log';
                break;
            }
                case URLS.FRONTDOOR_FRONTENDS: {
                this.subtitle = 'Frontends';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add a Frontend';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'frontends';
                break;
            }
            case URLS.FRONTDOOR_NAMESPACES: {
                this.subtitle = 'Namespaces';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add a Namespace';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'namespaces';
                break;
            }
            case URLS.FRONTDOOR_IPSEC_TUNNELS: {
                this.subtitle = 'IPsec Tunnels';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add an IPsec Tunnel';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'ipsec-tunnels';
                break;
            }
                case URLS.FRONTDOOR_AUTH_PROVIDERS: {
                this.subtitle = 'Authentication Providers';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add a Provider';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'frontdoor-providers';
                break;
            }
                case URLS.FRONTDOOR_CLIENT_CERTIFICATES: {
                this.subtitle = 'Client Certificates';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add a Clilent Certificate';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'certificates';
                break;
            }
                case URLS.FRONTDOOR_TOKENS: {
                this.subtitle = 'Certificate Request Tokens';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add a Clilent Certificate';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = false;
                this.showTabs = true;
                this.tabs = 'certificates';
                break;
            }
                case URLS.FRONTDOOR_HEALTH_CHECKS: {
                this.subtitle = 'Health Checks';
                this.breadcrumb = this.subtitle;
                this.actionTitle = 'Add a Health Check';
                this.isShaded = false;
                this.noPadding = false;
                this.isFilterable = true;
                this.showTabs = true;
                this.tabs = 'health-checks';
                break;
            }

            default: {
                if (this.currentNetwork.name !== null && this.currentNetwork.name !== undefined) {
                    this.subtitle = this.currentNetwork.name;
                } else {
                    this.subtitle = 'Dashboard';
                }
                this.isHeader = false;
                this.isFilterable = false;
                this.breadcrumb = 'Dashboard';
                this.showTabs = false;
                break;
            }
        }

        // Frontdoor detail routes (/frontdoor/http-shares/:id and friends) fall through
        // to `default`, which leaves isHeader false on purpose because those pages render
        // their own lib-form-header. Only the browser tab name needs correcting here —
        // without this it keeps whatever the previous page set ("Dashboard").
        if (BETA_LIST_PAGES.includes(url)) {
            this.isHeader = false;
            this.isFilterable = false;
        }
        const detailTitle = this.getFrontdoorDetailTitle(url);
        if (detailTitle !== undefined) {
            this.titleService.setTitle('Cloud Ziti: ' + detailTitle);
        } else if (this.subtitle !== undefined) {
            this.titleService.setTitle('Cloud Ziti: ' + this.subtitle);
        } else {
            this.titleService.setTitle('Cloud Ziti Console');
        }
        if (this.isLoggedIn) {
            this.zenDeskService.zE('webWidget', 'helpCenter:setSuggestions', { search: this.subtitle });
            this.zenDeskService.zE('webWidget', 'identify', {
                name: localStorage.getItem('profile_nick'),
                email: localStorage.getItem('profile_email'),
            });
            if (!this.isFirstPage) {
                this.zenDeskService.zE('webWidget', 'hide');
                this.isFirstPage = true;
            }
        } else {
            this.zenDeskService.zE('webWidget', 'hide');
        }
    }

    private handleCloudZitiAssets() {
        this.isZACView =
          (this.area === 'ZAC' && !this.currentNavItem.isMOPView) || this.currentNavItem.isZACView || (this.area === '' && this.menuService.lastArea === 'ZAC' && !this.currentNavItem.isMOPView);
    }

    private setFavicon(faviconUrl) {
        if (document != null && document.getElementById('appFavicon') != null) {
            document.getElementById('appFavicon').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple57').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple60').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple72').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple76').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple114').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple120').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple144').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple152').setAttribute('href', faviconUrl);
            document.getElementById('appFaviconApple180').setAttribute('href', faviconUrl);
            document.getElementById('appFavicon192').setAttribute('href', faviconUrl);
            document.getElementById('appFavicon32').setAttribute('href', faviconUrl);
            document.getElementById('appFavicon96').setAttribute('href', faviconUrl);
            document.getElementById('appFavicon16').setAttribute('href', faviconUrl);
        }
    }

    /**
     * Set the OrgID for the app
     */
    private async setOrganization(orgId) {
        // making sure the user is logged in before attempting to set the org
        if (this.isLoggedIn && orgId !== this.currentOrg.id) {
            const org = this.orgLookup[orgId];
            if (orgId != null && org != null) {
                localStorage.setItem('organizationId', orgId);
                this.logger.info('Setting ORG', org);
                this.currentOrg = new NetworkGroup(org);
                this.apiService.setCurrentOrg(this.currentOrg);
                this.featureService.checkFeatures(orgId);
                if (this.authorizationService.canListAzureSubscriptions()) {
                    this.getAzureSubscription(this.currentOrg);
                }
            } else {
                this.logger.info('OrgID is empty!');
            }
        } else {
            return;
        }
    }

    private async subscriptionChanged(subscription) {
        if (subscription?.subscriptionId) {
            this.checkOverdueStatus(subscription, this.isSelfService);
        }
        if (!this.isSelfService) {
            return;
        }
        if (this.featureService.showGettingStarted && !this.gettingStartedWasShown) {
            this.showGettingStarted();
        }
        this.isSelfServiceTeams = this.featureService.isSelfServiceTeamsTier(subscription);
        const isNewInvitedUser = localStorage.getItem('newInvitedUser_' + this.tenantLabel) === 'true';
        if (isNewInvitedUser && this.isSelfService) {
            this.customerService.setNewSelfServiceUserFlags(this.tenantLabel);
        }
        if (!isEmpty(this.currentPaymentProfile)) {
            this.checkPaymentMethodStatus(this.currentPaymentProfile);
        }
        this.handleBannerMessageVisibility();
    }

    private async paymentProfileChanged(paymentProfile) {
        if (this.isSelfService && !isEmpty(this.currentSubscription)) {
            this.checkPaymentMethodStatus(paymentProfile);
        }
    }

    private checkPaymentMethodStatus(paymentProfile) {
        if (
            !this.authorizationService.isSupportUser() &&
            !this.isEnterprise &&
            !this.isSelfServiceTeams &&
            paymentProfile?.paymentMethod?.status === 'INVALID'
        ) {
            this.apiService.setBillingStatusInvalid(true);
            this.billingInvalid = true;
            this.router.navigate([URLS.BILLING]);
        }
    }

    private getAzureSubscription(currentOrg: NetworkGroup) {
        this.apiService
            .getLinkedResource(currentOrg, 'azureSubscription')
            .toPromise()
            .then((result) => {
                this.wasAzureSubscriptionInitialized = true;
                this.hasAzureSubscription = false;
                if (
                    result['_embedded'] &&
                    result['_embedded']['azureSubscriptions'] &&
                    result['_embedded']['azureSubscriptions'].length > 0
                ) {
                    const azureSubscriptions = result['_embedded']['azureSubscriptions'];
                    this.hasAzureSubscription = true;
                    this.logger.info('Set Azure Subscription', azureSubscriptions[0]);
                    this.apiService.setCurrentAzureSubscription(new AzureSubscription(azureSubscriptions[0]));
                } else {
                    this.apiService.setCurrentAzureSubscription(new AzureSubscription({}));
                }
            });
    }

    private initNetworkLimits() {
        if (isEmpty(this.currentNetwork?.id)) {
            return;
        }
        this.networkServiceV2
            .getNetworkLimits(this.currentNetwork.id)
            .toPromise()
            .then((networkLimits: any) => {
                this.networkLimits = networkLimits?._embedded?.networkLimitList || [];
                const currentLimit = first(this.networkLimits);
                this.networkServiceV2.setCurrentNetworkLimit(currentLimit);
            });
    }

    private _initialNetworkLoad(combined) {
        this.isFirstLoad = false;
        let cookieSelfLink = null;
        let cookieNetwork = null;
        let network;
        // check if we have a network saved in cookies
        if (this.cookieService.get('currentNetwork')) {
            cookieSelfLink = this.cookieService.get('currentNetwork');
            // this.logger.info('COOKIE LINK', cookieSelfLink);
            cookieNetwork = combined.find((netwrk) => netwrk._links.self.href === cookieSelfLink);
        }
        // if we have a selfLink saved in cookies and we found a matching network, load it up
        if (cookieSelfLink && cookieNetwork) {
            this.logger.info('Load network from cookie');
            network = this.apiService.getNetworkModel(cookieNetwork);
            // else we didn't find any saved network in the cookies
        } else {
            this.logger.info('No cookie network found, load first network that is not in error');
            const ignoredNetworkStates = [500, 'ERROR', 'DELETING', 'DELETED'];
            let networkToSelect = combined.find((nw) => !ignoredNetworkStates.includes(nw.status));
            if (!networkToSelect) {
                networkToSelect = combined[0];
            }
            network = this.apiService.getNetworkModel(networkToSelect);
        }
        const currentNetworkId = this.apiService.getInitialNetworkId();
        const preferedNetworkId = this.identityPreferencesService.getPreference('default_network_id');
        let networkId;
        if (currentNetworkId) {
          networkId = currentNetworkId;
        } else {
          networkId = preferedNetworkId;
        }
        if (networkId) {
          let network;
          this.networks.forEach((nw) => {
            if (nw?.id === networkId) {
              network = nw;
            }
          });
          if (network) {
            this.apiService.setCurrentNetwork(network);
            return;
          }
        } else if (this.networks.length === 1) {
          this.currentNetwork = this.networks[0];
          this.apiService.setCurrentNetwork(this.currentNetwork);
          return;
        } else if (this.networks.length === 0) {
          return;
        }
        if(!this.pageIsUnauthed && !window.location.pathname.includes('/frontdoor')) {
        this.dialogRef = this.dialogForm.open(SelectNetworkModalComponent, {
          data: {
            networks: combined
          },
          autoFocus: false,
        });
        this.dialogRef.afterClosed().subscribe((result) => {
            if (result?.id) {
              this.apiService.setCurrentNetwork(result);
              if (result.status === 'PROVISIONING') {
                this.router.navigate([URLS.PROCESS_EXECUTIONS]);
              }
            } else if (result?.newNetwork) {
              const network: any = new Network({});
              network.name = 'Select a Network';
              network.isPlaceholder = true;
              this.apiService.setCurrentNetwork(network);
              this.dialogRef = this.dialogForm.open(V2NetworkCreationFormComponent, {
                minHeight: '100%',
                minWidth: '100%',
                height: '100%',
                width: '100%',
              });
            } else {
              const network: any = new Network({});
              network.name = 'Select a Network';
              network.isPlaceholder = true;
              this.apiService.setCurrentNetwork(network);
              this.router.navigate([URLS.NETWORKS_COMBINED]);
            }
        });
        }

    }

    private initDefaultApp() {
      const doAppInit = this.ignoredRoutes.includes(this.initRoute);
      if (!doAppInit) {
        return;
      }
      this.apiService.resetInitialNetworkId();
      const defaultApp = this.identityPreferencesService.getPreference('default_app_id');
      if (defaultApp) {
        this.menuService.setApp(defaultApp);
        let route = '/dashboard';
        switch (defaultApp) {
          case 'networks':
            route = this.featureService.dashboardRoute;
            break;
          case 'infrastructure':
            route = this.featureService.dashboardRoute;
            break;
          case 'organization':
            route = '/user-identities/users';
            break;
          case 'billing':
            route = '/billing';
            break;
          case 'support_operations':
            route = '/operations';
            break;
          case 'frontdoor':
            this.menuService.setArea('Frontdoor');
            this.sideNavigatorService.frontdoorReady$.pipe(take(1)).subscribe(() => {
                if (this.sideNavigatorService.isV2) {
                    this.router.navigate([URLS.FRONTDOOR_HTTP]);
                } else {
                    this.router.navigate([URLS.FRONTDOOR]);
                }
            });
            return; // skip router.navigate below — frontdoor navigates async via frontdoorReady$
          case 'on_prem':
            route = URLS.ON_PREM;
            break;

        }
        this.router.navigate([route]);
      }
    }

    private async _loadV7Networks() {
        return await this.networkServiceV2.getNetworks({ pageParams: { size: 2000, page: 0 }, params: { embed: 'network-controllers' } }).then(
            (networks: any) => {
                const zitiNetworks = [];
                for (const zitiNetwork of networks) {
                    // only show v2 networks that are in a living state
                    if (zitiNetwork.status !== 'DELETING' && zitiNetwork.status !== 'DELETED') {
                        zitiNetworks.push(zitiNetwork);
                    }
                }
                this.networks = zitiNetworks;
                this.networkInit.next(true);
                delay(() => {
                  this.networkServiceV2._networks.next(this.networks);
                }, 1000);
            }, // @TODO - better handling - 404's are ERROR but OK if no one has V2 networks yet
            (error) => {
                this.logger.error('Error returning V2 networks for app-component', error);
                this.networks = [];
            }
        );
    }

    private checkForZitiSession(attempts = 0) {
      attempts++;
      delay(() => {
          if (this.isZACView && !this.zitiControllerService.hasZitiSession && this.currentNetwork?.id) {
            if (attempts > 4) {
              this.bannerMessageZiti = 'Waiting to connect to ziti controller...';
              this.showBannerZiti = true;
            }
            this.checkForZitiSession(attempts);
          } else {
            this.showBannerZiti = false;
          }
      }, 2000);
    }

    private initZacGrowlerHandler() {
        if (growler) {
            const growlerError = growler?.error;
            growler.error = (message) => {
                if (this.zitiControllerService.hasZitiSession) {
                  growlerError(message);
                }
            };
        }
    }

    refreshProcesses() {
        clearTimeout(this.processRefresher);

        if (!this.currentNetwork?.id) {
          this.processRefresher = setTimeout(() => {
            this.refreshProcesses();
          }, this.processRefreshInterval);
          return;
        }

        const oldest = moment().subtract(30, 'minutes');
        const options = {
            params: {
                networkId: this.currentNetwork.id,
                status: 'RUNNING',
                startedAt: encodeURIComponent('>=' + oldest.toISOString()),
            },
            pageParams: {
                size: 1,
                page: 0,
            },
        };
        this.processExecutionService.getProcessExecutionPage(options, 'startedAt').then(
            (processes) => {
                this.processActive = processes.length > 0;
                this.processRefresher = setTimeout(() => {
                    this.refreshProcesses();
                }, this.processRefreshInterval);
            },
            () => {
                this.logger.error('Failed to retrieve process executions.');
            }
        );
    }

    initDarkmodeUrl() {
      const dateObject = new Date(this.version.buildTime);
      const timestamp = dateObject.getTime();
      const url = `/assets/styles/darkmode.css?v=${timestamp}`;
      this.darkModeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    addGreyscale() {
      try {
        const html = document.getElementsByTagName('body')[0];
        //this.renderer.setStyle(html, 'filter', 'grayscale(100%)');
      } catch (err) {
        console.log('html document not available');
      }
    }

    removeGreyscale() {
      try {
        const html = document.getElementsByTagName('body')[0];
        this.renderer.removeStyle(html, 'filter');
      } catch (err) {
        console.log('html document not available');
      }
    }

    initOrgBranding() {
      if (this.brandingIsSet) {
        return;
      }
      const html = document.getElementsByTagName('body')[0];

      let tenantLabel: any = localStorage.getItem('tenantLabel');
      if (_.isEmpty(tenantLabel)) {
        const siteName = window?.location?.hostname?.split('.')[0];
        if (siteName) {
          tenantLabel = siteName;
        } else {
          tenantLabel = undefined;
        }
      }

      this.brandingService.fetchBrandCss(tenantLabel.toLowerCase()).then((rawCss) => {
        html.style.cssText = rawCss;
        this.brandingIsSet = true;
      }).finally(() => {
        this.brandingService.orgBrandingInit = true;
        this.removeGreyscale();
      });
    }

    updateCurrentCustomer() {
        const c = new Customer();
        c.name = 'Unknown';
        this.apiService.setCurrentCustomer(c);
        if (this.authorizationService.canListCustomers()) {
            this.customerService.getSelfCustomer(this.apiService.theTenantIs.id).then((customers) => {
                const customerList = customers as Customer[];
                if (customerList.length > 0) {
                    this.customerInit = true;
                    this.apiService.setCurrentCustomer(customerList[0]);
                } else {
                    console.log('Unable to get current customer');
                }
            });
        } else {
            this.customerInit = true;
        }
    }

    updateCurrentTenant() {
      if (!this.authorizationService.canListTenants()) {
        return of({});
      }
      if (this.tenantInit) {
        return of({});;
      }
      const label = localStorage.getItem('tenantLabel');
      if (!_.isEmpty(label)) {
        this.tenantInit = true;
        return of({});
      }
      return this.iamService.get('tenants', 'self').pipe(tap(
        {
          next: (tenant) => {
            this.apiService.setCurrentTenant(new Tenant(tenant));
            this.tenantInit = true;
          },
          complete: () => {
            this.appLoading = false;
          }
          }
      ))
    }

    updateCurrentOrganization() {
      if (this.orgInit) {
        return of({});
      }
      return this.iamService.restoreSelectedOrganization().pipe(
        tap({
          next: (org) => {
            this.orgInit = !!org;
          },
          complete: () => {
            this.appLoading = false;
          },
        })
      );
    }

    updateCurrentUser(): Promise<any> {
      if (this.authorizationService.obtainedMopPermissions) {
        return Promise.resolve({});
      }
      return this.authorizationService.getSilentAuthorization();
    }

    getExecutions() {
        const network = this.currentNetwork as NetworkV2;
        // Fetch for in-flight network creation, and for controllers being added to an
        // existing HA network (the network stays PROVISIONED while a controller provisions).
        const inFlight = network.status === "PROVISIONING" ||
            (network.status === "PROVISIONED" && (network as any).haEnabled);
        if ((inFlight && this.authorizationService.canCreateNetworks(network.networkGroupId) && this.authorizationService.canListNetworks()) || this.authorizationService.canListCustomers()) {
            this.getProcessExecutions();
        }
    }

    async getProcessExecutions() {

        this.showProcessBanner = false;
        this.processBannerMessage = '';
        this.fetchUpdatedData().then(
            async (results: any) => {
                for(const result of results) {
                    const isNetworkCreation = result.name === createNetworkString;
                    const isControllerCreation = (result.resourceType || '').replace(/-/g, '').toLowerCase() === 'networkcontroller';
                    if(isNetworkCreation || isControllerCreation) {
                      const steps = result?._meta?.processData?.steps;
                      this.handleProcessAlerts(steps, isControllerCreation);
                    }
                }
            },
            (error) => {
                console.log(error);
                this.growlService.show(new GrowlerData('error', 'Cannot find Processes. '));
            }
        );
    }

    fetchUpdatedData(): Promise<any[]> {
        const options = {
            params: {
                networkId: this.currentNetwork.id,
                status: 'RUNNING',
                // No name filter: the add-controller process has a dynamic name
                // ("Create <name> network controller in <region>"); we match it
                // client-side by resourceType in getProcessExecutions.
                meta: "processData"
            },
            pageParams: {
                size: 10,
                page: 0,
            },
        };
        return this.processExecutionService.getProcessExecutionPage(options);

    }

    handleProcessAlerts(steps: any, isController = false) {
        // get the Create Network Controller step
        for(const step of steps) {
            if(step?.status === "STARTED" && !step?.subprocess) {
                // if the step is one of the DNS validation steps and it has started, return true
                if( dnsValidationSteps.includes(step.stepName) && step.status === "STARTED") {
                    this.showDnsValidationInstructions(isController);
                    break;
                } else if(hybridCloudConnectionCheckString === step.stepName && step.status === "STARTED" && step.retryCount > 0) {
                    this.showHybridCloudConnectionAlert();
                    break;
                } else if(verifyHostConnectivityString === step.stepName && step.status === 'STARTED' && step.retryCount > 0) {
                    this.showVerifyHostConnectivityAlert(step);
                    break;
                }
            } else if (step?.subprocess) {
                this.handleProcessAlerts(step.subprocess.steps, isController);
            }
        };
    }

    showDnsValidationInstructions(isController = false) {
        this.showProcessBanner = true;
        this.dnsAlertIsController = isController;
        this.processBannerMessage = isController
            ? `A network controller is waiting on custom DNS records to be provisioned`
            : `Your Network creation has some issues that require action`;
        this.processBannerType = DNS_ALERT;

        if(sessionStorage.getItem(`${dnsValidationSessionString}${this.currentNetwork.getId()}`) == null) {

            this.dialogRef = this.dialogForm.open(NetworkControllerInfoComponent, {
                data: { network: this.currentNetwork, isController: isController },
                height: '500px',
                width: '800px',
                autoFocus: false,
            });

            this.dialogRef.afterClosed().subscribe((result) => {
                sessionStorage.setItem(`${dnsValidationSessionString}${this.currentNetwork.getId()}`, this.currentNetwork.getId());
            });
        }
    }

    showHybridCloudConnectionAlert() {
        this.showProcessBanner = true;
        this.processBannerMessage = `Your Network creation has some issues that require action`;
        this.processBannerType = HYBRID_CLOUD_CONNECTION_ALERT;

        if(sessionStorage.getItem(`${hybridCloudSessionString}${this.currentNetwork.getId()}`) == null) {

            this.dialogRef = this.dialogForm.open(HybridCloudRegistrationAlertComponent, {
                data: {
                    network: this.currentNetwork,
                    networkController: (this.currentNetwork as unknown as NetworkV2).networkController
                },
                height: '500px',
                width: '800px',
                autoFocus: false,
            });

            this.dialogRef.afterClosed().subscribe((result) => {
                sessionStorage.setItem(`${hybridCloudSessionString}${this.currentNetwork.getId()}`, this.currentNetwork.getId());
            });
        }
    }

    showVerifyHostConnectivityAlert(step) {
        this.showProcessBanner = true;
        this.processBannerMessage = `Your Network creation has some issues that require action`;
        this.processBannerType = HOST_CONNECTIVITY_ALERT;
        this.alertStep = step;

        if(sessionStorage.getItem(`${hostConnectivitySessionString}${this.currentNetwork.getId()}`) == null) {

            this.dialogRef = this.dialogForm.open(PrecheckDetailsComponent, {
                data: {
                    step: step,
                    title: "Unable to Validate Host Connectivity",
                    showIcon: true
                },
                maxHeight: '90vh',
                width: '800px',
                autoFocus: false,
            });

            this.dialogRef.afterClosed().subscribe((result) => {
                sessionStorage.setItem(`${hostConnectivitySessionString}${this.currentNetwork.getId()}`, this.currentNetwork.getId());
            });
        }
    }


    bannerClosed() {
        this.showBanner = false;
    }

    processBannerClosed() {
        this.showProcessBanner = false;
        this.processBannerType = '';
        this.processBannerMessage = '';
        this.alertStep = '';
    }

    zitiBannerClosed() {
        this.showBannerZiti = false;
    }
}
